// src/terms/TermsOfUseContent.jsx
//
// Texto integral dos Termos de Uso, Política de Privacidade e TCLE.
// Compartilhado entre:
//   • src/TermsOfUse.jsx       (fluxo de cadastro com aceite)
//   • src/TermsOfUsePublic.jsx (página pública /termos-de-uso, sem aceite)
//
// IMPORTANTE: este arquivo é a fonte canônica do texto. O script
// `functions/scripts/buildTermsPlainFromJsx.js` lê este arquivo e
// gera `functions/legal/termos-v1.0.txt` (texto plano usado pelo
// backend para hash de evidência de consentimento e PDFs).
//
// Mantenha os marcadores `TERMS_CONTENT_BEGIN` e `TERMS_CONTENT_END`
// inalterados — eles delimitam o trecho extraído pelo script.
//
// Ao alterar o conteúdo, lembre de:
//   1. Rodar `node functions/scripts/buildTermsPlainFromJsx.js`
//   2. Incrementar TERMS_OF_USE_VERSION em:
//      • src/terms/termsVersion.js
//      • functions/legal/termosConsultaMaisV1.js
//   3. Build + deploy (hosting e apiV2).
//
// BROADCAST: NÃO é obrigatório para atualizações rotineiras de texto
// (clarificações, informações adicionais, ajustes de redação). Só
// considerar `_broadcast_terms_update.js` quando o stakeholder pedir
// explicitamente comunicado em massa à base — não sugira nem insista
// nesse passo por conta própria.

import React from 'react';

const TermsOfUseContent = () => (
  <>
    {/* TERMS_CONTENT_BEGIN */}
    <p>
      <strong>
        Termos de Uso, Política de Privacidade e Termo de Consentimento
        Livre e Esclarecido (TCLE) – Consulta+
      </strong>
      <br />
      <br />
      Estes Termos regulam o uso da plataforma Consulta+ (“Plataforma”),
      que oferece atendimento médico digital por chat, inclusive
      emissão de documentos médicos (receitas, atestados, orientações),
      nos termos da legislação brasileira aplicável à telemedicina, às
      relações de consumo e à privacidade de dados pessoais.
      <br />
      <br />
      Ao continuar, você declara que leu, compreendeu e concorda com:
      (i) os Termos de Uso; (ii) a Política de Privacidade; (iii) o
      Termo de Consentimento Livre e Esclarecido (TCLE) para
      teleatendimento; e (iv) as declarações éticas e limitações do
      serviço.
      <br />
      <br />
      Se você não concordar integralmente, deve interromper o acesso e
      não prosseguir.
    </p>

    <p>
      <strong>1. O que é a Plataforma e o que ela faz</strong>
      <br />
      <br />
      1.1. A Consulta+ é um serviço digital que facilita contato entre
      médicos e pacientes, permitindo orientação médica remota de forma
      assíncrona (por mensagens escritas, sem garantia de resposta
      imediata em tempo real). O atendimento pode incluir avaliação
      clínica à distância, esclarecimento de dúvidas, orientações
      gerais em saúde e, quando clinicamente adequado e permitido, a
      emissão de documentos médicos digitais válidos em território
      nacional, assinados por profissional médico habilitado e
      identificado.
      <br />
      <br />
      1.1.1. O atendimento médico na Plataforma é realizado por médico
      clínico geral habilitado, atualmente identificado como{' '}
      <strong>Dr. Guilherme Esmanhotto — CRM 23.706/DF</strong>. O
      paciente declara estar ciente de que a consulta não constitui
      atendimento por médico especialista titulado (RQE), podendo o
      clínico geral avaliar e conduzir a maioria das queixas clínicas
      e, quando clinicamente indicado, orientar encaminhamento para
      cuidados especializados presenciais.
      <br />
      <br />
      1.2. A Plataforma atua como infraestrutura tecnológica e canal de
      comunicação. A conduta clínica (orientar, prescrever, solicitar
      exames, sugerir afastamento etc.) é ato privado do médico, dentro
      de sua autonomia técnica e ética, conforme regulamentação do
      Conselho Federal de Medicina (CFM). A Plataforma, como ambiente,
      não garante que um documento médico será emitido, nem garante
      afastamento laboral ou prescrição de medicamentos controlados.
      <br />
      <br />
      1.3. A Plataforma não substitui pronto-atendimento presencial,
      emergência ou urgência. Situações que envolvem sofrimento intenso,
      risco imediato, perda de consciência, sinais neurológicos
      agudos, dor torácica intensa, falta de ar importante, sangramento
      ativo, trauma grave, risco de suicídio ou qualquer quadro que
      você entenda como potencialmente grave exigem busca imediata de
      atendimento presencial de urgência.
      <br />
      <br />
      1.4. <strong>Ausência de prazo máximo de início e de
      resposta.</strong> A natureza assíncrona significa que pode haver
      intervalo entre a confirmação do pagamento e o início efetivo da
      conversa com o médico responsável, bem como entre cada mensagem e a
      resposta seguinte ao longo da consulta. A Plataforma{' '}
      <strong>não assume prazo máximo</strong> para o início do
      atendimento, não assume prazo máximo para o envio de respostas e{' '}
      <strong>não garante atendimento em horário determinado</strong>: o
      que se contrata é teleconsulta assíncrona por chat, e não
      atendimento em tempo real. O paciente declara que compreende a
      natureza assíncrona do serviço e aceita aguardar o início e o
      andamento do atendimento, desde que seu quadro não seja uma
      urgência ou emergência (cláusulas 1.3, 3.6.1 e 4.1). Enquanto a
      consulta não tiver sido iniciada (cláusula 1.4.2), permanece
      assegurado o direito de cancelamento com reembolso integral pelos
      canais oficiais (cláusula 5.3).
      <br />
      <br />
      1.4.1. <strong>Por que o atendimento é assíncrono.</strong> A
      teleconsulta oferecida é de natureza assíncrona (troca de mensagens
      de texto), o que significa que médico e paciente não precisam estar
      conectados ao mesmo tempo: cada um escreve e responde quando lhe
      for possível, com flexibilidade de horário. É justamente essa
      flexibilidade recíproca que viabiliza o principal diferencial do
      serviço — um <strong>canal de comunicação direto com o médico
      responsável, aberto por 48 (quarenta e oito) horas
      consecutivas</strong> (cláusula 3.5) —, o que não ocorre na
      consulta presencial nem na teleconsulta por vídeo, ambas limitadas
      ao tempo de duração do próprio encontro. A contrapartida desse
      modelo é a ausência de resposta imediata e de prazo máximo de
      resposta (cláusulas 1.4, 3.6 e 4.1). Os documentos emitidos
      (atestados, receitas, encaminhamentos) têm validade a partir da
      data de início do chat, independentemente do momento em que o
      médico efetivamente responder.
      <br />
      <br />
      1.4.2. <strong>Início efetivo da consulta.</strong> Para todos os
      fins legais e contratuais destes Termos — inclusive para
      delimitação do direito de arrependimento e da política de
      reembolso descritos nas cláusulas 5.3 e 5.5 — considera-se{' '}
      <strong>iniciada a consulta</strong> no momento em que o médico
      responsável praticar, pela primeira vez, qualquer um dos
      seguintes atos no canal de atendimento, o que ocorrer primeiro:
      <br />
      (i) o envio, no chat, do texto de{' '}
      <strong>conduta detalhada</strong> — assim entendida a mensagem
      que apresenta ao paciente, de forma individualizada, a{' '}
      <strong>hipótese diagnóstica principal</strong>, as{' '}
      <strong>orientações gerais</strong> de cuidado e os{' '}
      <strong>sinais de alerta</strong> que exigem reavaliação ou busca
      de atendimento presencial, acompanhada ou não de plano
      terapêutico, prescrição e encaminhamento;<br />
      (ii) o envio de qualquer documento médico relacionado ao caso
      (atestado, receita, pedido de exame, laudo, encaminhamento ou
      orientação por escrito); ou<br />
      (iii) o envio, pelo próprio médico responsável, de qualquer outra
      mensagem escrita no chat a respeito do caso — por exemplo,
      pergunta de esclarecimento, solicitação de imagem ou de exame
      anterior e orientação pontual —, por já representar atuação
      clínica individualizada sobre o atendimento.
      <br />
      <br />
      <strong>Não</strong> configuram início efetivo da consulta as
      mensagens automáticas do sistema (confirmação de pagamento,
      lembretes, avisos operacionais) nem as perguntas da triagem
      automatizada anteriores à atuação do médico.
    </p>

    <p>
      <strong>2. Quem pode usar e responsabilidade pelas informações</strong>
      <br />
      <br />
      2.1. Para utilizar a Plataforma você deve:
      <br />
      • informar nome completo verdadeiro;<br />
      • ter plena capacidade civil OU estar assistido/representado por
      responsável legal; e<br />
      • concordar que todas as respostas fornecidas durante a triagem
      inicial (anamnese guiada por chat) são verdadeiras, completas e
      refletem seu quadro atual.
      <br />
      <br />
      2.2. Se você estiver utilizando em nome de outra pessoa (por
      exemplo, filho menor), você declara ser o responsável legal e
      garante a veracidade das informações clínicas prestadas.
      <br />
      <br />
      2.3. Você concorda em:
      <br />
      • não omitir sintomas importantes;<br />
      • não manipular sintomas apenas para tentar obter afastamento ou
      medicação específica;<br />
      • atualizar o médico caso seu quadro mude (ex.: piora súbita,
      surgimento de novos sinais de gravidade);<br />
      • <strong>ler atentamente as orientações médicas enviadas por
      escrito</strong> no chat ou em documentos, com{' '}
      <strong>especial atenção</strong> a avisos de busca de pronto
      atendimento, atendimento presencial prioritário, sinais de
      gravidade, contraindicações e demais alertas clínicos indicados
      pelo médico;<br />
      • <strong>buscar pronto atendimento presencial</strong> (ou outro
      serviço de urgência/emergência indicado){' '}
      <strong>quando orientado para tal</strong> pelo médico, deixando
      de depender exclusivamente da teleconsulta nessas hipóteses.
      <br />
      <br />
      2.4. A omissão de dados relevantes, a simulação de sintomas, a
      alteração deliberada de informações, o envio de documentos
      falsos ou o envio de imagens não autênticas (por exemplo, fotos
      obtidas na internet, de domínio público ou de terceiros,
      apresentadas como se fossem do próprio paciente — cláusula 3.10)
      pode resultar em recusa de emissão de atestado/receita,
      interrupção do atendimento e eventual comunicação às autoridades
      competentes, nos termos da lei.
      <br />
      <br />
      2.5. <strong>Obrigação de enviar documento quando
      solicitado.</strong> Sempre que a Plataforma, o médico responsável
      ou o canal oficial de atendimento solicitar o envio de documento,
      o paciente obriga-se a enviá-lo, de forma nítida e legível, pelo
      canal indicado no pedido. O não envio, a recusa ou o envio
      incompleto, ilegível ou que não corresponda ao titular{' '}
      <strong>poderá ensejar, nesses casos, o cancelamento dos
      documentos médicos já emitidos</strong> na consulta correspondente
      — invalidação formal, nos termos da cláusula 3.13 —, sem prejuízo
      das demais medidas previstas nestes Termos.
      <br />
      <br />
      2.6. <strong>Dever de comprovar a identidade.</strong> É dever do
      paciente comprovar a própria identidade mediante o envio de{' '}
      <strong>documento oficial</strong> (em rol exemplificativo e não
      exaustivo: RG, CNH ou passaporte), sempre que a Plataforma, o
      médico responsável ou o canal oficial de atendimento o solicitar,
      inclusive para reembolso pelos canais oficiais (cláusulas 5.3 e
      5.5) e para o exercício de direitos sobre dados pessoais (cláusula
      7.3). O não envio desse documento oficial quando pedido aplica a
      cláusula 2.5 e poderá ensejar, nesses casos, o cancelamento dos
      documentos médicos já emitidos.
    </p>

    <p>
      <strong>3. Como funciona o fluxo de atendimento</strong>
      <br />
      <br />
      3.1. Fluxo básico:
      <br />
      (a) você informa dados de identificação e aceita estes termos;<br />
      (b) você realiza o pagamento da consulta;<br />
      (c) você responde à triagem inicial (AnamneseChat), conduzida por
      assistente virtual e/ou fluxos automatizados;<br />
      (d) um médico humano revisa as informações e dá seguimento no
      chat médico (ConsultaChat), faz perguntas, solicita
      esclarecimentos e, se clinicamente cabível, fornece orientações,
      receitas digitais, atestados ou recomenda avaliação presencial.
      <br />
      <br />
      3.2. <strong>A consulta é prestada exclusivamente por chat, mediante
      troca de mensagens de texto, admitida a complementação por imagens
      </strong> (por exemplo, fotos de lesões, exames, laudos ou documentos
      clínicos). Esse é o escopo da contratação e da teleconsulta: não há
      atendimento por chamada de vídeo, ligação telefônica ou áudio. O
      paciente obriga-se a enviar as imagens solicitadas pelo médico quando
      este as considerar necessárias à avaliação clínica. <strong>Toda
      imagem enviada deve ser própria, autêntica e atual</strong> — isto é,
      retratar o próprio paciente (ou a pessoa assistida, na hipótese da
      cláusula 2.2), o seu quadro clínico real e o momento presente —,
      conforme detalhado na cláusula 3.10. O médico pode
      julgar que, sem exame físico presencial, não é possível concluir um
      diagnóstico seguro e poderá orientar comparecimento presencial.
      <br />
      <br />
      3.3. A emissão de atestado médico, indicação de CID, afastamento
      laboral, prescrição de medicamentos ou solicitação de exames NÃO
      É AUTOMÁTICA. O médico só emitirá se houver justificativa clínica
      suficiente, dentro da ética e das normas do CFM. Isso significa
      que pode haver consulta sem emissão de atestado, afastamento,
      CID, antibiótico, controlado ou laudo — sem direito automático a
      reembolso. O pagamento deve ocorrer dentro de 5 dias do início da consulta, caso contrário, a consulta expirará e não será mais considerada para efeitos de emissão de atestado, receita médica etc.
      <br />
      <br />
      3.4. Validade dos documentos: as receitas e atestados emitidos
      digitalmente pelo médico responsável seguem os requisitos
      legais aplicáveis à prescrição e à certificação médica remota,
      incluindo identificação do profissional, CRM e assinatura
      válida em formato eletrônico aceito. A aceitação prática desses
      documentos por terceiros (empregador, escola, plano de saúde,
      farmácia etc.) depende também das regras próprias de cada
      instituição. A Plataforma não garante aceitação por terceiros que
      imponham requisitos adicionais ou restritivos.
      <br />
      <br />
      3.4.1. <strong>Prévias e exemplos demonstrativos.</strong> Antes da
      confirmação do pagamento, a Plataforma pode exibir uma{' '}
      <strong>prévia meramente ilustrativa</strong> de como o documento
      será apresentado. Essa prévia é um{' '}
      <strong>exemplo demonstrativo, não é um documento médico</strong> e{' '}
      <strong>não possui validade legal alguma</strong>: seu conteúdo é
      genérico, não corresponde à conduta clínica definida para o seu
      caso, não é assinada digitalmente pelo médico, não contém código de
      validação e não pode ser impressa, compartilhada ou apresentada a
      empregador, escola, farmácia, laboratório, plano de saúde, órgão
      público ou qualquer terceiro. Somente o documento oficial, emitido
      após a confirmação do pagamento e assinado digitalmente pelo médico
      responsável (padrão ICP-Brasil), tem validade. O uso de imagem,
      captura de tela ou reprodução de prévia como se documento médico
      fosse caracteriza <strong>falsidade documental</strong>, sujeita às
      sanções legais cabíveis, e configura violação destes Termos, com
      bloqueio do acesso à Plataforma.
      <br />
      <br />
      3.5. <strong>Compromisso de canal aberto por 48 horas e ausência
      de consulta de retorno.</strong> A partir do início da conversa com
      o médico (ConsultaChat), após a confirmação do pagamento, a
      Plataforma <strong>compromete-se a manter aberto o canal de
      comunicação direto com o médico responsável por 2 (dois) dias
      corridos (48 horas consecutivas)</strong>, contados desse início.
      Nesse período, você pode sair e retornar à conversa quando quiser,
      dentro dessa janela.
      <br />
      <br />
      Esse compromisso diz respeito à <strong>abertura e à manutenção do
      canal</strong>, e <strong>não</strong> a prazo máximo de resposta:
      dentro da janela, as mensagens do médico podem ocorrer a qualquer
      momento, nos termos das cláusulas 1.4, 1.4.1 e 3.6.
      <br />
      <br />
      O prazo de 48 horas pode ser <strong>encerrado antes do seu
      término</strong> nas hipóteses a seguir, sem que isso configure
      descumprimento deste compromisso nem gere direito automático a
      reembolso:
      <br />
      • <strong>encerramento pelo médico ou pela Plataforma em razão de
      infração a estes Termos</strong> — por exemplo, fraude, envio de
      imagem não autêntica, ofensa, xingamento, assédio, ameaça, conduta
      indigna, uso abusivo do canal ou pedidos repetitivos sobre assunto
      já respondido de forma clara e objetiva (cláusulas 3.10, 4.3, 4.5
      e 4.6);<br />
      • <strong>finalização da consulta pelo próprio paciente</strong>,
      pelos recursos disponíveis no aplicativo; e<br />
      • <strong>encerramento a pedido do paciente</strong>, manifestado
      no chat ou pelos canais oficiais de atendimento.
      <br />
      <br />
      Ao término do prazo de 48 horas, ou na ocorrência de qualquer das
      hipóteses acima, o chat é <strong>encerrado</strong> e{' '}
      <strong>não pode ser reaberto</strong>
      — inclusive por link salvo, retorno ao site ou contato com a
      equipe de suporte. Os documentos médicos já emitidos (receita,
      atestado, laudo etc.) permanecem válidos conforme a legislação
      aplicável e, em regra, são enviados também ao e-mail informado
      no cadastro, independentemente do encerramento do chat.
      <br />
      <br />
      <strong>Não há direito a consulta de retorno</strong> — ou seja,
      não existe reavaliação clínica gratuita, prorrogação de
      atestado, ajuste ou segunda via com alteração de conteúdo
      clínico, prescrição complementar, esclarecimento médico sobre o
      mesmo episódio ou qualquer continuidade de atendimento após o
      encerramento do chat ou após o prazo de 48 horas. Qualquer nova
      demanda clínica vinculada ao atendimento anterior ou a um novo
      quadro exige <strong>nova consulta paga</strong>, iniciada do
      zero na Plataforma.
      <br />
      <br />
      3.6. <strong>Ausência de compromisso de resposta em prazo
      máximo.</strong> Durante toda a consulta, inclusive depois de o
      médico encaminhar a conduta clínica e os documentos médicos
      cabíveis ao caso (quando clinicamente devidos — por exemplo,
      atestado, receitas, pedidos de exame e encaminhamentos), o médico{' '}
      <strong>não está obrigado a responder dentro de qualquer prazo
      máximo</strong>, ressalvado apenas o compromisso de manutenção do
      canal pelas 48 (quarenta e oito) horas previstas na cláusula 3.5.
      Isso decorre da natureza assíncrona da teleconsulta (cláusulas 1.1,
      1.4, 1.4.1 e 3.5): as respostas não precisam ser imediatas nem em
      tempo real, podendo ocorrer a qualquer momento dentro da janela de
      48 horas.
      <br />
      <br />
      A ausência de prazo máximo não dispensa o médico do{' '}
      <strong>dever de diligência</strong>: o profissional{' '}
      <strong>compromete-se a envidar seus melhores esforços para
      responder ao paciente em prazo razoável</strong>, observada a
      necessidade de analisar o caso, revisar as informações prestadas e
      elaborar respostas clinicamente adequadas e seguras, o que
      naturalmente demanda tempo e não se confunde com resposta
      imediata.
      <br />
      <br />
      Pela mesma razão, a <strong>insatisfação com o tempo de
      resposta</strong> enquanto a consulta estiver dentro da janela de
      48 horas <strong>não configura falha do serviço</strong> nem gera
      direito a reembolso, nos termos da cláusula 5.3.
      <br />
      <br />
      3.6.1. <strong>Assincronia, urgência e emergência.</strong> Porque
      não há compromisso de resposta em prazo máximo, a Plataforma deixa
      claro que a teleconsulta assíncrona por chat, embora possa oferecer
      apoio, avaliação, orientação e documentos médicos quando
      clinicamente cabíveis, <strong>não é adequada ao manejo completo de
      situações de urgência ou emergência</strong>. Diante de quadro de
      maior gravidade, de piora súbita ou de qualquer dos sinais
      indicados na cláusula 1.3, o paciente deve{' '}
      <strong>buscar imediatamente pronto atendimento presencial</strong>,
      sem aguardar resposta no chat.
      <br />
      <br />
      3.7. <strong>Recusa de envio de imagem considerada fundamental ao
      diagnóstico.</strong> Quando o médico considerar que o envio de uma
      imagem (por exemplo, foto de lesão de pele, garganta, olho ou região
      acometida) é fundamental para a definição do diagnóstico clínico, a
      recusa do paciente em fornecê-la poderá comprometer a segurança
      diagnóstica. Nessa hipótese, em razão da incerteza diagnóstica
      resultante, o médico poderá, no exercício de sua autonomia técnica e
      ética, <strong>recusar-se a emitir os documentos médicos
      correspondentes</strong> (por exemplo, atestados e receitas), sem
      que isso configure falha do serviço nem gere direito automático a
      reembolso.
      <br />
      <br />
      3.8. <strong>Impossibilidade técnica ou de outra natureza de envio
      das imagens solicitadas.</strong> Caso o paciente, por incapacidade
      técnica ou de qualquer outra natureza, não consiga enviar as imagens
      solicitadas pelo médico, este poderá declarar que as informações
      disponíveis são insuficientes para prosseguir com segurança a
      consulta e recomendar atendimento presencial. Essa recomendação, por
      decorrer da impossibilidade de avaliação adequada à distância, não
      configura falha do serviço e <strong>não dá ao paciente direito a
      reembolso</strong> do valor pago.
      <br />
      <br />
      3.9. <strong>Formato dos documentos médicos (receitas, exames,
      encaminhamentos e atestados).</strong> O médico não se obriga a
      emitir ou enviar receitas, pedidos de exame, encaminhamentos ou
      atestados em formato exato ou específico solicitado pelo paciente. O
      médico compromete-se a enviar esses documentos em formato usual,
      comumente aceito pela maioria das farmácias, laboratórios e clínicas
      diagnósticas. A eventual inconformidade ou insatisfação do paciente
      com o formato da prescrição, do exame, do encaminhamento ou do
      atestado <strong>não dá direito a reembolso</strong> do serviço.
      <br />
      <br />
      3.10. <strong>Autenticidade das imagens enviadas.</strong> Todas as
      imagens enviadas durante a triagem ou a consulta devem ser{' '}
      <strong>próprias, autênticas e atuais</strong>: devem retratar o
      próprio paciente (ou a pessoa assistida, na hipótese da cláusula
      2.2), o seu quadro clínico real e o momento presente do episódio
      relatado. É <strong>expressamente proibido</strong> enviar, como se
      fossem do paciente, imagens obtidas na internet, de bancos de
      imagens, de domínio público, de terceiros, geradas ou alteradas por
      software, ou fotos antigas apresentadas como atuais. O envio de
      imagem não autêntica constitui <strong>fraude e violação de
      má-fé</strong> destes Termos (cláusulas 2.1, 2.4 e 4.3) e poderá
      ensejar, <strong>observado o procedimento da cláusula 3.10.5</strong>:
      <br />
      • o <strong>encerramento imediato da consulta, sem direito a
      reembolso</strong>, sendo o valor destinado à cobertura de
      infraestrutura e à compensação do médico pelo tempo já dedicado,
      nos moldes da cláusula 4.5;<br />
      • a <strong>recusa de emissão</strong> ou a <strong>invalidação
      clínica</strong> dos documentos médicos que tenham se baseado na
      imagem fraudulenta;<br />
      • a <strong>suspensão do acesso à Plataforma por prazo
      indeterminado</strong>, comunicada mediante{' '}
      <strong>notificação extrajudicial</strong> enviada
      ao e-mail cadastrado — podendo ser reavaliada a qualquer tempo, na
      forma da cláusula 3.10.5, alíneas (iv) e (vi);<br />
      • a <strong>multa compensatória de 7 (sete) vezes o valor pago pela
      consulta</strong>, prevista na cláusula 3.10.1;<br />
      • a <strong>comunicação às autoridades competentes</strong>,
      nos termos da lei;<br />
      • a responsabilização civil do paciente pelos danos causados à
      Plataforma, ao médico e a terceiros.
    </p>

    <p>
      3.10.1. <strong>ATENÇÃO — MULTA DE 7 (SETE) VEZES O VALOR DA
      CONSULTA EM CASO DE FRAUDE (CLÁUSULA PENAL).</strong>
      <br />
      (i) <strong>Hipóteses.</strong> A prática dolosa de qualquer das
      condutas abaixo sujeita o paciente ao pagamento de{' '}
      <strong>multa compensatória equivalente a 7 (sete) vezes o valor
      pago pela consulta</strong> em que a conduta ocorrer — ou, se essa
      consulta tiver sido gratuita, 7 (sete) vezes o preço da consulta
      vigente na data da conduta:
      <br />
      (a) enviar <strong>imagem não autêntica</strong>, nos termos das
      cláusulas 3.10, 3.10.2, 3.10.3 e 3.10.4 — inclusive imagem criada ou
      alterada por inteligência artificial, foto antiga apresentada como
      atual, imagem obtida na internet ou de terceiro e foto de
      medicamento ou receita de terceiro —, apurada na forma da cláusula
      3.10.5;<br />
      (b) apresentar à Plataforma ou ao médico{' '}
      <strong>documento médico falso, adulterado ou que imite documento
      emitido pela Plataforma</strong>;<br />
      (c) <strong>alterar documento médico emitido pela
      Plataforma</strong> — por exemplo, datas, número de dias de
      afastamento, código CID ou nome do paciente;<br />
      (d) <strong>utilizar documento formalmente invalidado</strong>, em
      descumprimento da cláusula 3.13.1; ou<br />
      (e) utilizar <strong>identidade de terceiro</strong> ou{' '}
      <strong>documento de identificação falso ou adulterado</strong>{' '}
      para cadastro, atendimento, emissão de documento ou reembolso.
      <br />
      (ii) <strong>Fundamento do valor.</strong> O valor da multa{' '}
      <strong>não corresponde ao preço da consulta</strong>. Corresponde à
      estimativa, feita nesta cláusula, do conteúdo econômico do dever de
      veracidade e de autenticidade assumido pelo paciente, cuja violação
      produz, entre outros: o tempo do médico dedicado a atendimento
      dirigido por informação falsa e à reavaliação do caso; a apuração
      técnica (análise de metadados e de proveniência dos arquivos), a
      preservação da prova e a expedição de notificação; a invalidação de
      documentos e a resposta a verificações de autenticidade feitas por
      terceiros; e a exposição do médico e da Plataforma a apuração
      ético-profissional e a responsabilização perante terceiros. É esse
      conteúdo econômico, e não o preço da consulta, o valor da obrigação
      a que se refere o art. 412 do Código Civil.
      <br />
      (iii) <strong>Natureza.</strong> A multa é{' '}
      <strong>cláusula penal compensatória</strong> (arts. 408, 409 e 416
      do Código Civil). Sua exigência{' '}
      <strong>independe de prova de prejuízo</strong> (art. 416, caput) e
      ela vale como mínimo da indenização: a Plataforma e/ou o médico
      podem exigir os danos que a excedam, desde que comprovados (art.
      416, parágrafo único, e arts. 186, 927 e 944 do Código Civil).
      <br />
      (iv) <strong>Uma multa por consulta.</strong> Incide uma única
      multa por consulta, ainda que a conduta envolva mais de uma imagem
      ou documento.
      <br />
      (v) <strong>Comunicação e exigibilidade.</strong> A multa é
      comunicada por <strong>notificação extrajudicial</strong> enviada ao
      e-mail cadastrado, que informa o valor e o meio de pagamento, e só é
      exigível <strong>depois de transcorrido o prazo de
      esclarecimentos</strong> aberto pela notificação. Acolhidos os
      esclarecimentos, a multa é <strong>cancelada</strong> e o valor
      eventualmente pago é <strong>restituído integralmente</strong>. Na
      hipótese da alínea (a), observa-se o procedimento da cláusula
      3.10.5.
      <br />
      (vi) <strong>Independência em relação à esfera criminal.</strong>{' '}
      A multa tem natureza <strong>exclusivamente contratual</strong>. O
      pagamento <strong>não revoga a suspensão</strong>, não importa
      reconhecimento de crime e <strong>não afasta nem condiciona</strong>{' '}
      a comunicação às autoridades competentes; o não pagamento não dá
      causa a nenhuma dessas providências. Não paga, a multa é cobrada
      pelas vias legais, com correção monetária e juros de mora.
      <br />
      (vii) <strong>Aplicação no tempo.</strong> Esta cláusula aplica-se
      às condutas praticadas depois do aceite da versão 2.37 destes
      Termos.
    </p>

    <p>
      3.10.2. <strong>Atualidade das imagens e verificação por
      metadados.</strong> O paciente <strong>compromete-se expressamente a
      enviar somente fotos atuais do seu quadro clínico</strong>, isto é,
      capturadas no momento presente do episódio relatado na triagem ou na
      consulta. A imagem cujos <strong>metadados do arquivo</strong> (por
      exemplo, data e hora de captura) ou outros elementos técnicos
      indiquem ter sido capturada em momento anterior, incompatível com o
      episódio relatado, e que tenha sido apresentada como atual, é
      considerada <strong>imagem não autêntica</strong> para todos os fins
      destes Termos — equiparando-se ao envio de imagem obtida na
      internet, de domínio público ou de terceiros — e sujeita o paciente
      às       <strong>mesmas medidas e consequências das cláusulas 3.10 e
      3.10.1</strong>, incluindo o encerramento imediato da consulta sem
      direito a reembolso, a suspensão do acesso à Plataforma e a multa da
      cláusula 3.10.1, observado o procedimento da cláusula 3.10.5.
      <br />
      <br />
      3.10.3. <strong>Fotos de medicamentos e receitas (caixas,
      cartelas, frascos e prescrições).</strong> Sem prejuízo do
      disposto nas cláusulas 3.10 a 3.10.2, ao enviar fotografias de
      caixas, cartelas, frascos ou embalagens de medicamentos, bem como
      de receitas ou prescrições anteriores, o paciente{' '}
      <strong>compromete-se expressamente</strong> a que tais imagens:
      <br />
      (i) sejam <strong>fotos pessoais</strong>, capturadas pelo próprio
      paciente (ou pelo responsável legal, na hipótese da cláusula 2.2),
      de itens que estejam em sua posse legítima;<br />
      (ii) <strong>representem fiel e verdadeiramente o uso atual e
      pessoal do medicamento</strong> — isto é, medicamento efetivamente
      em uso pelo paciente (ou pela pessoa assistida), na apresentação,
      dose e posologia reais do tratamento em curso; e<br />
      (iii) não sejam empregadas para sugerir tratamento inexistente,
      histórico de prescrição que não lhe pertença ou continuidade de
      uso que não corresponda à sua realidade clínica.
      <br />
      <br />
      O paciente obriga-se, ainda, a <strong>abster-se de qualquer
      conduta apta a induzir o médico a erro</strong> por meio dessas
      imagens — incluindo, em rol exemplificativo, o envio de fotos de
      medicamentos ou receitas de terceiros como se fossem próprios, de
      embalagens antigas ou vazias apresentadas como de uso corrente, de
      imagens obtidas na internet ou de receitas adulteradas —, ciente
      de que tais imagens influenciam diretamente a avaliação clínica e
      as decisões de prescrição e de renovação de receitas (cláusula
      3.12). O envio de foto de medicamento ou de receita em desacordo
      com esta cláusula <strong>equipara-se a imagem não
      autêntica</strong> para todos os fins destes Termos, sujeitando o
      paciente às mesmas medidas e consequências das cláusulas 3.10,
      3.10.1 e 3.10.2.
      <br />
      <br />
      3.10.4. <strong>Imagens geradas ou tratadas por inteligência
      artificial.</strong>
      <br />
      (i) <strong>Equipara-se a imagem não autêntica</strong>, para todos
      os fins das cláusulas 3.10 e 3.10.1, a imagem <strong>criada</strong>{' '}
      por sistema de inteligência artificial generativa e apresentada como
      retrato do próprio paciente (ou da pessoa assistida, na hipótese da
      cláusula 2.2) e do momento presente do episódio relatado;<br />
      (ii) <strong>não</strong> configura, por si só, imagem não autêntica
      a fotografia verdadeira do próprio paciente que tenha passado por
      realce, aproximação, recorte, remoção de elemento do plano de fundo
      ou melhoria automática de qualidade, ainda que tais recursos
      empreguem inteligência artificial e registrem essa informação nos
      metadados do arquivo; nessa hipótese, a Plataforma poderá solicitar
      o arquivo original;<br />
      (iii) a marcação de origem sintética nos metadados do arquivo
      constitui <strong>indício, e não prova</strong>, de inautenticidade,
      e <strong>não autoriza, por si, as medidas da cláusula 3.10</strong>:
      antes de qualquer medida, o paciente é questionado no próprio
      atendimento, na forma da cláusula 3.10.5, alínea (i), e a informação
      de que a imagem não é própria ou não é atual{' '}
      <strong>afasta a infração</strong>; a violação se caracteriza quando o
      paciente <strong>confirma</strong> que a imagem é própria e atual e,
      ainda assim, subsiste o elemento objetivo registrado no arquivo; e
      <br />
      (iv) é <strong>permitido</strong> o envio de imagem meramente{' '}
      <strong>ilustrativa</strong>, desde que o paciente informe
      expressamente essa condição; imagem ilustrativa não constitui
      evidência clínica e não substitui fotografia do quadro real.
      <br />
      <br />
      3.10.5. <strong>Procedimento de apuração.</strong> As medidas da
      cláusula 3.10 <strong>são precedidas</strong> do procedimento a
      seguir:
      <br />
      (i) <strong>Questionamento prévio, no próprio atendimento.</strong>{' '}
      Identificado elemento objetivo registrado no arquivo ou na própria
      imagem — data de captura anterior ao episódio relatado, declaração de
      criação por inteligência artificial no manifesto do arquivo, ou
      localização da imagem na internet, em banco de imagens ou em acervo de
      domínio público —, o paciente é{' '}
      <strong>questionado no chat</strong> e convidado a confirmar que a
      imagem é sua e foi capturada nas últimas 24 horas. Nenhuma medida da
      cláusula 3.10 é adotada antes dessa pergunta;
      <br />
      (ii) a informação de que a imagem <strong>não</strong> é própria ou
      <strong> não</strong> é atual, bem como o reenvio de imagem adequada,{' '}
      <strong>afasta a infração</strong>: não se abre apuração nem se
      registra nada;<br />
      (iii) <strong>confirmada</strong> pelo paciente a autoria e a
      atualidade da imagem, e subsistindo o elemento objetivo da alínea (i),
      configura-se a <strong>divergência</strong> entre o que foi afirmado e
      o que o arquivo registra, que caracteriza a violação. A consulta é
      encerrada e a Plataforma aplica a{' '}
      <strong>suspensão do acesso por prazo indeterminado</strong>, comunicada
      por <strong>uma única notificação extrajudicial</strong> enviada ao
      e-mail cadastrado, que dá ciência das{' '}
      <strong>providências cíveis e criminais cabíveis</strong> e abre prazo{' '}
      <strong>facultativo de 5 (cinco) dias úteis para
      esclarecimentos</strong>. A mesma notificação comunica a{' '}
      <strong>multa da cláusula 3.10.1</strong>, com o meio de pagamento,
      exigível só depois desse prazo, e <strong>não exige</strong> do
      paciente o envio de arquivo original nem de nova fotografia — a
      pergunta já lhe foi feita na alínea (i);<br />
      (iv) os esclarecimentos são <strong>livres em forma e
      conteúdo</strong> e apreciados por <strong>pessoa natural</strong>;
      acolhidos, a suspensão é <strong>revogada</strong>, a multa da
      cláusula 3.10.1 é <strong>cancelada</strong>, com restituição
      integral do que tiver sido pago, e a apuração é arquivada sem
      registro de infração. Não há segunda
      notificação, e o <strong>silêncio do paciente não agrava</strong> a
      medida já aplicada;<br />
      (v) a leitura dos metadados do arquivo é automatizada, mas{' '}
      <strong>não decide isoladamente</strong>: a apuração é conduzida por
      pessoa natural, assegurado ao paciente o direito de{' '}
      <strong>revisão</strong> e de informação sobre os critérios
      utilizados (art. 20 da Lei nº 13.709/2018). Aberta a apuração, a
      imagem e os respectivos dados técnicos são{' '}
      <strong>conservados como prova</strong> do procedimento, em ambiente
      controlado e pelo prazo legal de guarda do prontuário;<br />
      (vi) a suspensão pode ser <strong>reavaliada a qualquer
      tempo</strong>, inclusive depois do prazo da alínea (iii); e<br />
      (vii) as medidas alcançam <strong>o(a) paciente apurado(a)</strong>;
      terceiro que compartilhe telefone ou endereço eletrônico com o
      cadastro apurado tem o acesso restabelecido mediante comprovação de
      identidade própria (cláusula 2.6).
      <br />
      <br />
      3.11. <strong>Data dos documentos médicos e fuso horário.</strong>{' '}
      A Plataforma registra a data e a hora das consultas e dos documentos
      médicos pelo <strong>horário oficial de Brasília</strong>, que é a
      referência padrão dos registros do sistema e da assinatura digital.
      O <strong>horário de início do chat</strong> é o critério padrão de
      aferição do início da consulta. O paciente que, no momento da
      consulta, se encontre em localidade brasileira de{' '}
      <strong>fuso horário legal distinto</strong> do de Brasília (Lei nº
      12.876/2013) e solicite que a data inicial do afastamento indicada
      em atestado corresponda à data vigente no seu fuso (dia anterior ou
      posterior ao registrado pelo sistema) deverá{' '}
      <strong>comprovar razoavelmente a localidade alegada</strong>, por
      meio de documentação idônea — em rol exemplificativo e não
      exaustivo: comprovante de residência recente (conta de luz, água,
      gás, telefone ou internet), faturas de outra natureza com endereço
      na localidade, comprovante de hospedagem ou de passagem. A
      concessão do ajuste é <strong>ato médico discricionário</strong>,
      condicionado à apresentação de prova razoável, e será registrada em
      prontuário com a respectiva fundamentação. A recusa fundamentada do
      ajuste não configura falha do serviço e{' '}
      <strong>não dá direito a reembolso</strong>. A alegação falsa de
      localização para obtenção de alteração de data constitui violação
      de má-fé destes Termos (cláusulas 2.1 e 4.3), sujeitando o paciente
      às medidas cabíveis, incluindo a invalidação clínica do documento e
      a comunicação às autoridades competentes.
      <br />
      <br />
      3.11.1. <strong>Horário limítrofe da virada do dia.</strong> Nos
      casos em que o chat se inicia logo após a meia-noite (horário de
      Brasília) e o paciente alega ter iniciado o cadastro ou a triagem
      ainda no dia anterior, o médico{' '}
      <strong>poderá, a seu exclusivo critério e em caráter
      excepcional</strong> — sem que isso constitua obrigação —, solicitar
      à área técnica da Plataforma a verificação dos registros de sistema
      (data e hora de início do cadastro ou da triagem) e, confirmado o
      registro fidedigno de início na data anterior, conceder o
      afastamento a partir dessa data, com a devida anotação em
      prontuário. Permanece como padrão, para todos os fins, o horário de
      início do chat; a verificação técnica é faculdade do médico, e a
      sua não realização ou o seu resultado negativo não configuram falha
      do serviço nem dão direito a reembolso.
      <br />
      <br />
      3.12. <strong>Duração padrão das renovações de receita.</strong>{' '}
      Quando a receita emitida na Plataforma for{' '}
      <strong>renovação de tratamento iniciado por outro médico</strong>{' '}
      (isto é, quando o médico da Consulta+ <strong>não for o prescritor
      original</strong>), a renovação é feita,{' '}
      <strong>por padrão, para o equivalente a 1 (um) mês de uso</strong>{' '}
      do medicamento. Essa disposição tem por finalidade preservar a
      segurança do paciente, favorecer a reavaliação clínica periódica e
      respeitar a continuidade responsável do plano terapêutico
      estabelecido pelo prescritor original. Em caráter excepcional, e no
      exercício da autonomia técnica e ética do médico assistente, a
      renovação poderá abranger prazo superior, observados os limites e a
      fundamentação previstos na política clínica interna da Plataforma.
      A aplicação do padrão de 1 (um) mês{' '}
      <strong>não configura falha do serviço</strong> e{' '}
      <strong>não dá direito a reembolso</strong>.
      <br />
      <br />
      3.13. <strong>Invalidação de documentos médicos emitidos.</strong>{' '}
      A Plataforma, por meio do médico responsável e/ou do Diretor
      Técnico, <strong>pode invalidar formalmente</strong>, a qualquer
      tempo após a emissão, documentos médicos produzidos no âmbito da
      consulta (incluindo atestados, receitas, encaminhamentos,
      solicitações de exame e relatórios), quando houver: (i) violação
      destes Termos (em especial das cláusulas 2.1, 2.4, 2.5, 2.6, 3.10,
      3.10.1, 3.10.2, 3.11 e 4.3); (ii) indícios fundados de fraude,
      falsidade, adulteração ou uso indevido do documento ou da
      consulta; (iii) lastro em imagem ou informação não autêntica; (iv)
      decisão fundamentada de natureza contratual ou ético-profissional
      que torne insubsistente a manutenção do documento como comprovante
      válido; ou (v) não envio de documento solicitado, ou omissão em
      comprovar a identidade mediante documento oficial quando pedido
      (cláusulas 2.5 e 2.6). A invalidação é <strong>ato formal do emissor</strong> e
      poderá ser comunicada ao paciente pelos canais oficiais da
      Plataforma e refletida nos mecanismos de verificação de
      autenticidade disponibilizados (incluindo o Easycheck), bem como
      pelo <strong>cancelamento do acesso ao arquivo original via
      link</strong> eventualmente enviado. A comunicação da invalidação
      informa o <strong>status formal</strong> do documento e{' '}
      <strong>não implica</strong>, por si só, divulgação de dados
      clínicos ou do motivo detalhado da decisão a terceiros.
      <br />
      <br />
      3.13.1. <strong>Compromisso de não utilizar documento
      invalidado.</strong> O paciente <strong>compromete-se
      expressamente</strong> a <strong>não apresentar, anexar, reenviar,
      invocar nem de qualquer forma utilizar</strong>, perante
      empregador, escola, instituição de ensino, plano de saúde,
      autoridade pública, farmácia, laboratório, clínica ou qualquer
      terceiro, documento médico que tenha sido{' '}
      <strong>formalmente invalidado</strong> pela Plataforma ou pelo
      médico emissor — inclusive cópia impressa, arquivo PDF já baixado,
      captura de tela, fotografia do documento ou qualquer reprodução.
      O descumprimento deste compromisso constitui{' '}
      <strong>violação de má-fé</strong> destes Termos (cláusulas 2.1,
      2.4 e 4.3) e poderá configurar, em tese e sem prejuízo da
      tipificação cabível às autoridades competentes, ilícito civil e/ou
      penal (incluindo, conforme o caso, os arts. 171, 299 e 304 do
      Código Penal).
      <br />
      <br />
      3.13.2. <strong>Consequências pelo uso de documento
      invalidado.</strong> Sem prejuízo das demais medidas já previstas
      nestes Termos, a utilização ou tentativa de utilização de
      documento médico invalidado sujeita o paciente, a critério da
      Plataforma e/ou do médico responsável, a:
      <br />
      • a <strong>comunicação às autoridades competentes</strong> e, no
      que couber e for legalmente permitido, a terceiros legitimamente
      interessados na autenticidade do documento (por exemplo, pelo
      canal de verificação de autenticidade),{' '}
      <strong>limitada ao status formal</strong> de invalidação e sem
      revelação de conteúdo clínico;<br />
      • a <strong>multa compensatória de 7 (sete) vezes o valor pago pela
      consulta</strong>, prevista na cláusula 3.10.1, alínea (i)(d);<br />
      • a responsabilização civil pelos danos causados à Plataforma, ao
      médico e a terceiros.
    </p>

    <p>
      <strong>4. Ausência de prazo de resposta, limitações e uso
      adequado</strong>
      <br />
      <br />
      4.1. A Plataforma informa de forma clara e prévia que{' '}
      <strong>não há prazo máximo</strong> para o início efetivo da
      conversa após o pagamento nem para o envio de cada resposta ao
      longo da consulta (cláusulas 1.4, 1.4.1, 3.5 e 3.6). Essa é a
      característica própria da modalidade assíncrona por chat e é
      também a condição que permite manter o canal direto com o médico
      aberto por 48 (quarenta e oito) horas consecutivas, prazo muito
      superior ao da consulta presencial ou por vídeo. Se o seu caso é
      urgente ou emergencial, ou se a espera própria da modalidade
      assíncrona for incompatível com a sua necessidade, você deve
      procurar pronto atendimento presencial imediatamente (cláusulas
      1.3 e 3.6.1). Enquanto a consulta não tiver sido iniciada
      (cláusula 1.4.2), você pode cancelar e receber reembolso integral
      pelos canais oficiais (cláusula 5.3).
      <br />
      <br />
      4.2. O paciente declara entender que enviar mensagens em volume
      excessivo ou de forma repetida não antecipa nem acelera a
      resposta: as mensagens são lidas e respondidas ao longo da janela
      de 48 horas da consulta, sem prazo máximo (cláusulas 3.5 e 3.6).
      <br />
      <br />
      4.3. A Plataforma pode, sempre que julgar necessário, encerrar ou
      pausar o atendimento caso:
      <br />
      • identifique risco iminente à vida/saúde que exija presença
      física imediata;<br />
      • identifique abuso, assédio, agressão verbal, ameaças ou uso
      inadequado do canal;<br />
      • identifique tentativa de fraude, uso indevido de atestados,
      receitas ou falsificação de laudos.
      <br />
      <br />
      4.4. <strong>Conduta decorosa e urbanidade.</strong> O paciente
      compromete-se a interagir com médicos, equipe de apoio e sistemas
      da Plataforma de modo decoroso, respeitoso e com urbanidade,
      abstendo-se de linguagem ofensiva, insultos, xingamentos,
      humilhações, discriminação, assédio, ameaças ou qualquer conduta
      que atente contra a dignidade dos profissionais de saúde, da
      equipe de atendimento ou de terceiros no canal de atendimento
      (incluindo chat da consulta, chat de suporte, e-mail e demais
      canais oficiais da Plataforma).
      <br />
      <br />
      4.5. <strong>Ofensas, conduta indigna, suspensão e encerramento
      sem reembolso.</strong> Ofensas, xingamentos, humilhações,
      discriminação, assédio, ameaças ou qualquer conduta indigna
      proferidos ao médico, à equipe de apoio ou a terceiros nos canais
      de atendimento da Plataforma — inclusive durante ou após a
      consulta — ensejam, a critério da Plataforma e/ou do médico
      responsável:
      <br />
      • a <strong>finalização imediata da consulta, sem direito a
      reembolso</strong> do valor pago, que será utilizado para a
      cobertura dos gastos de infraestrutura tecnológica da Plataforma
      e compensação do médico pelo tempo e disponibilidade já dedicados
      ao atendimento, nos limites permitidos pela legislação
      aplicável;<br />
      • a <strong>suspensão do acesso à Plataforma por prazo
      determinado ou indeterminado</strong>, comunicada mediante{' '}
      <strong>notificação extrajudicial</strong> enviada ao e-mail
      cadastrado — eventual reavaliação ou restabelecimento do acesso
      ficando a critério exclusivo da Plataforma —, sem prejuízo da
      multa prevista na cláusula 4.5.1;<br />
      • a <strong>comunicação às autoridades competentes</strong>, nos
      termos da lei;<br />
      • a responsabilização civil do paciente pelos danos causados à
      Plataforma, ao médico, à equipe de apoio e a terceiros.
      <br />
      <br />
      4.5.1. <strong>Multa compensatória por ofensa e conduta indigna
      (cláusula penal).</strong> Sem prejuízo das medidas previstas nas
      cláusulas 4.3, 4.4 e 4.5, a prática de ofensas, xingamentos,
      humilhações, discriminação, assédio, ameaças ou conduta indigna
      sujeita o paciente ao pagamento de <strong>multa compensatória
      equivalente a 1 (uma) vez o valor pago pela consulta</strong>,
      a título de cláusula penal compensatória (arts. 408 a 416 do
      Código Civil), pela violação do dever de urbanidade e respeito
      assumido nestes Termos. Nos termos do art. 416 do Código Civil, a
      exigência da multa <strong>independe de comprovação de
      prejuízo</strong>. Fica convencionado, na forma do parágrafo
      único do art. 416 do Código Civil, que a multa vale como{' '}
      <strong>mínimo de indenização</strong>, podendo a Plataforma
      e/ou o médico exigir indenização suplementar pelos danos
      comprovadamente excedentes (por exemplo, custos de apuração,
      danos à reputação profissional e transtornos à equipe de
      atendimento).
      <br />
      <br />
      4.6. <strong>Pedidos repetitivos e perturbação do
      atendimento.</strong> Pedidos repetitivos relacionados ao mesmo
      assunto que já tenha sido respondido de forma clara e objetiva pelo
      médico podem ensejar o <strong>encerramento da consulta</strong>, a
      critério da Plataforma e/ou do médico responsável, por
      caracterizarem uso inadequado do canal e perturbação dos demais
      atendimentos. O encerramento nessa hipótese não gera direito
      automático a reembolso, observado o disposto na cláusula 5.
    </p>

    <p>
      <strong>5. Pagamento, caráter do serviço e política de
      reembolso</strong>
      <br />
      <br />
      5.1. A consulta é um serviço profissional de saúde prestado por
      médico habilitado. O pagamento é devido pela disponibilidade do
      profissional, pela análise clínica individualizada e pela
      orientação fornecida, mesmo que o desfecho não seja o que o
      paciente esperava (por exemplo: negativa de afastamento laboral
      extenso ou de antibiótico).
      <br />
      <br />
      5.2. Ao pagar, você concorda que está contratando avaliação
      médica por chat e não “compra garantida de atestado/receita/CID”.
      <br />
      <br />
      5.3. Reembolsos:
      <br />
      • <strong>Direito de arrependimento e cancelamento com reembolso
      automático antes do início efetivo da consulta (cláusula
      1.4.2):</strong> você faz jus ao{' '}
      <strong>reembolso integral do valor pago, por arrependimento e
      independentemente de justificativa</strong> (art. 49 do Código de
      Defesa do Consumidor), <strong>a qualquer momento antes do início
      efetivo da consulta</strong> — isto é, enquanto o médico
      responsável ainda não tiver enviado a conduta detalhada, documento
      médico relacionado ao caso ou mensagem própria sobre o caso no
      chat (cláusula 1.4.2). Os pedidos
      de reembolso ocorrem pelos canais oficiais a seguir: (i) pela{' '}
      <strong>ferramenta de cancelamento com reembolso automático</strong>{' '}
      no próprio aplicativo (card “Mudou de ideia?” / botão “Cancelar
      consulta e receber reembolso” na tela do chat), que processa o
      reembolso de forma automática ao ser utilizada e confirmada; ou
      (ii) por e-mail dirigido a{' '}
      <strong>atendimento@consultamais.app</strong>, mediante
      verificação de identidade (cláusulas 2.5 e 2.6). <strong>Pedidos isolados de
      reembolso</strong> — tais como mensagem isolada no chat da
      consulta ou e-mail isolado sem a confirmação e a verificação de
      identidade exigidas — <strong>não ensejarão reembolso
      automático</strong>. Reclamações ou registros em canais externos
      — incluindo, sem limitação, Procon, Reclame Aqui e demais
      plataformas de reclamação ou órgãos de defesa do consumidor —{' '}
      <strong>não substituem</strong> os canais oficiais de cancelamento
      com reembolso automático previstos nesta cláusula e{' '}
      <strong>não interrompem, por si sós</strong>, o atendimento. Tais
      registros serão analisados pela Plataforma. Se o paciente, após o
      registro externo, <strong>continuar a participar da consulta até a
      sua conclusão</strong> (cláusula 1.4.2),{' '}
      <strong>não haverá direito a reembolso automático</strong> por
      arrependimento ou mera insatisfação com o desfecho, ressalvados o
      direito de arrependimento do art. 49 do Código de Defesa do
      Consumidor enquanto a consulta ainda não tiver sido iniciada, os
      casos de vício do serviço e as hipóteses expressas desta cláusula
      5.3. Pedido claro de cancelamento ou reembolso formulado{' '}
      <strong>antes do início efetivo</strong> será tratado como tal,
      ainda que veiculado por canal externo, observado o prazo legal
      aplicável. Ao utilizar o canal oficial e concluir o procedimento,
      você receberá o <strong>reembolso integral</strong> do valor pago,
      no mesmo meio de pagamento utilizado, de forma automática,
      respeitados os prazos operacionais do banco/operadora (PIX:
      geralmente minutos a poucas horas em dias úteis; cartão de
      crédito: 1 a 2 ciclos de fatura, conforme o banco emissor). A
      ferramenta no chat permanece disponível enquanto a consulta não
      tiver sido iniciada. Após o início efetivo (cláusula 1.4.2),
      aplicam-se as demais regras desta cláusula 5.3 e a cláusula 5.5;
      <br />
      • Caso, após o pagamento, você seja orientado IMEDIATAMENTE e de
      forma objetiva a buscar emergência presencial (por entender-se que
      seu caso não é adequado para teleatendimento) sem que tenha havido
      efetiva atuação clínica assíncrona (isto é, o médico não chegou a
      conduzir avaliação detalhada, nem emitiu orientações específicas,
      nem revisou seu caso), a Plataforma pode analisar reembolso;
      <br />
      • Situações em que houve avaliação clínica, troca de mensagens,
      aconselhamento e conduta médica não dão direito automático a
      reembolso, mesmo que o médico decida não emitir o documento
      desejado pelo paciente;
      <br />
      • <strong>Insatisfação com o tempo de resposta dentro da janela
      de 48 horas:</strong> reclamação fundada exclusivamente na demora
      de resposta do médico, enquanto a consulta estiver em curso dentro
      do prazo de 48 horas (cláusula 3.5),{' '}
      <strong>não dá direito a reembolso</strong>, por ser inerente à
      teleconsulta assíncrona contratada, que não tem prazo máximo de
      resposta (cláusulas 1.4, 1.4.1, 3.6 e 4.1) e cuja flexibilidade é
      justamente o que viabiliza o canal direto com o médico por 48
      horas consecutivas. Ficam ressalvados o direito de arrependimento
      enquanto a consulta não tiver sido iniciada (cláusula 1.4.2) e as
      hipóteses de vício do serviço;
      <br />
      • Encerramento da consulta por ofensa ao médico, à equipe de
      apoio ou conduta indigna (cláusulas 4.5 e 4.5.1): sem direito a
      reembolso, podendo ainda ensejar suspensão de acesso e multa
      compensatória de 1 (uma) vez o valor pago pela consulta,
      sendo o valor da consulta destinado à cobertura de infraestrutura
      e compensação do profissional;
      <br />
      • Eventuais divergências sobre cobrança serão tratadas conforme o
      Código de Defesa do Consumidor, preservado o direito de defesa da
      Plataforma e do profissional contra alegações injustas ou má-fé.
      <br />
      <br />
      5.4. <strong>Contestação de pagamento (chargeback) após a
      prestação do serviço.</strong> A Plataforma disponibiliza canal
      oficial de cancelamento com reembolso automático integral enquanto
      a consulta ainda não tiver sido iniciada (cláusulas 5.3 e 5.5).
      Por esse motivo, a abertura de contestação, chargeback, disputa
      ou estorno junto ao banco, à bandeira, à operadora do cartão ou
      ao meio de pagamento, depois de o serviço ter sido prestado ou
      depois de o paciente ter deixado de exercer o cancelamento pelos
      canais oficiais até o início efetivo (cláusula 1.4.2), é
      considerada, em regra, <strong>contestação abusiva</strong> —
      uso do mecanismo de chargeback em desvio da finalidade de
      proteção contra fraude ou falha de cobrança, para obter a
      devolução do valor de um serviço já disponibilizado ou já
      prestado.
      <br />
      <br />
      5.4.1. <strong>O que não é contestação abusiva.</strong> Não se
      considera abusiva, para os fins desta cláusula: (i) o reembolso
      iniciado pelo próprio paciente pelos canais oficiais da
      Plataforma antes do início efetivo (cláusula 5.3); (ii) o
      reembolso iniciado pelo médico ou pela Plataforma; (iii) a
      contestação fundada em cobrança duplicada, valor divergente do
      contratado, transação não reconhecida por fraude de terceiro ou
      outro vício objetivo de cobrança, quando comunicada à Plataforma
      e comprovada; e (iv) as hipóteses de vício do serviço e os
      direitos irrenunciáveis previstos no Código de Defesa do
      Consumidor.
      <br />
      <br />
      5.4.2. <strong>Bloqueio a partir de 1 (uma) contestação
      abusiva.</strong> A ocorrência de <strong>1 (uma) ou mais</strong>{' '}
      contestações abusivas vinculadas à identidade do paciente
      (e-mail, CPF ou telefone, inclusive em cadastros distintos)
      autoriza a Plataforma a <strong>bloquear imediatamente</strong> a
      contratação de novas consultas e a recusar novos pagamentos pelo
      aplicativo, até a regularização prevista nesta cláusula. O
      bloqueio independe de o paciente ter recebido atestado, receita
      ou outro documento: basta a prestação ou a disponibilização do
      serviço (cláusulas 1.4.2 e 5.1) associada à reversão externa do
      pagamento.
      <br />
      <br />
      5.4.3. <strong>Obrigação de quitar a consulta não paga.</strong>{' '}
      A contestação abusiva não extingue a obrigação de pagar o
      serviço prestado ou disponibilizado. O paciente permanece
      devedor do <strong>valor integral da consulta contestada</strong>{' '}
      (ou da soma das consultas contestadas, se houver mais de uma),
      no preço vigente à época de cada contratação. Os registros da
      conversa, do prontuário e dos documentos emitidos poderão ser
      usados como prova da prestação do serviço.
      <br />
      <br />
      5.4.4. <strong>Juros de 1% (um por cento) ao mês.</strong> Sobre
      o valor de cada consulta contestada e não quitada incidem{' '}
      <strong>juros simples de 1% (um por cento) ao mês</strong>,
      calculados <strong>pro rata die</strong> com base no mês
      comercial de 30 (trinta) dias, a contar da data em que a
      contestação, o chargeback ou a disputa for registrada pelo meio
      de pagamento (ou, na falta dessa data, da data em que o status
      de contestação for detectado pela Plataforma) até a data da
      regularização. Os juros remuneram a mora pelo valor da consulta
      deixado sem pagamento após a reversão externa (arts. 389, 395,
      406 e 407 do Código Civil). A Plataforma exibirá, no momento da
      regularização, o valor principal, os juros acumulados e o total
      devido.
      <br />
      <br />
      5.4.5. <strong>Regularização self-service via PIX.</strong> O
      paciente poderá retomar o acesso quitando o valor devido
      (principal + juros) <strong>diretamente na Plataforma</strong>,
      pela opção “Regularizar via PIX”, disponível ao tentar iniciar
      nova consulta. O pagamento de regularização é aceito{' '}
      <strong>somente via PIX</strong> (meio irrevogável), para evitar
      novo chargeback sobre a própria quitação. Cartão de crédito ou
      débito não é oferecido nesse fluxo. A confirmação do PIX
      restaura o acesso <strong>automaticamente</strong>, sem
      necessidade de contato com o suporte. A regularização quita a
      pendência da consulta anterior; a contratação de nova consulta
      exige novo pagamento, no preço vigente.
      <br />
      <br />
      5.4.6. <strong>Outras formas de regularização.</strong> Se a
      contestação tiver sido aberta por engano, o paciente poderá
      solicitar ao seu banco ou operadora o cancelamento da
      contestação. Após a Plataforma confirmar que o pagamento
      original foi restabelecido ou que a disputa foi encerrada em
      favor da Plataforma, o acesso poderá ser liberado sem novo
      pagamento. Dúvidas ou casos particulares devem ser dirigidos a{' '}
      atendimento@consultamais.app.
      <br />
      <br />
      5.4.7. <strong>Efeitos enquanto a pendência estiver em
      aberto.</strong> Enquanto o valor devido (principal + juros) não
      for quitado e não houver liberação pela via da cláusula 5.4.6,
      novos pagamentos pelo aplicativo não serão aceitos e o paciente
      não poderá contratar nova consulta. O bloqueio não apaga o
      prontuário nem os documentos já emitidos.
      <br />
      <br />
      5.4.8. <strong>Independência em relação a outras medidas.</strong>{' '}
      O disposto nesta cláusula 5.4 não impede a Plataforma de adotar,
      quando cabível, as demais medidas previstas nestes Termos
      (suspensão, bloqueio por violação, comunicação a autoridades,
      a multa da cláusula 5.4.9, a notificação extrajudicial e a
      cobrança judicial da cláusula 5.4.10, e indenização suplementar
      pelos danos excedentes, inclusive taxas de chargeback cobradas
      pelo meio de pagamento), observados os direitos irrenunciáveis
      do consumidor.
      <br />
      <br />
      5.4.9. <strong>Multa compensatória por chargeback indevido
      (cláusula penal).</strong> Sem prejuízo do principal (cláusula
      5.4.3) e dos juros (cláusula 5.4.4), a contestação abusiva
      (cláusula 5.4) após a prestação ou a disponibilização do serviço
      sujeita o paciente ao pagamento de <strong>multa compensatória
      equivalente a 1 (uma) vez o valor da consulta
      contestada</strong>, a título de cláusula penal compensatória
      (arts. 408 a 416 do Código Civil). A multa remunera a violação
      da boa-fé objetiva (art. 422 do Código Civil), o exercício
      abusivo do direito de contestar (art. 187 do Código Civil) e o
      enriquecimento sem causa obtido com a reversão do pagamento de
      serviço já prestado ou já disponibilizado (arts. 884 e 885 do
      Código Civil), sobretudo quando existia canal oficial de
      reembolso não utilizado (cláusulas 5.3 e 5.5). Nos termos do
      art. 416 do Código Civil, a exigência da multa{' '}
      <strong>independe de comprovação de prejuízo</strong>. Fica
      convencionado, na forma do parágrafo único do art. 416 do
      Código Civil, que a multa vale como <strong>mínimo de
      indenização</strong>, podendo a Plataforma e/ou o médico exigir
      indenização suplementar pelos danos comprovadamente excedentes
      (por exemplo, taxas de chargeback do meio de pagamento, custas,
      honorários e danos à reputação). A multa é exigível, em
      especial, quando a Plataforma tiver de expedir{' '}
      <strong>notificação extrajudicial</strong> ou promover{' '}
      <strong>cobrança judicial</strong> (cláusula 5.4.10). A
      desistência tempestiva da contestação com restabelecimento do
      pagamento original, ou a regularização via PIX do principal e
      dos juros (cláusula 5.4.5) <strong>antes</strong> da notificação
      extrajudicial, poderá, a critério da Plataforma, afastar a
      cobrança da multa, sem prejuízo do bloqueio enquanto a pendência
      estiver em aberto.
      <br />
      <br />
      5.4.10. <strong>Notificação extrajudicial e cobrança
      judicial.</strong> O Departamento Jurídico da Plataforma poderá
      notificar o(a) paciente, para todos os efeitos, no e-mail
      cadastrado, reservando-se o direito de adotar todas as medidas
      cabíveis para impedir enriquecimento sem causa ou ilícito
      decorrente de chargeback indevido e para receber o crédito. A
      notificação extrajudicial constitui, quando cabível, interpelação
      para constituição em mora (art. 397, parágrafo único, do Código
      Civil). Não regularizada a obrigação, a Plataforma poderá
      promover cobrança judicial pelo rito cabível (Lei n.º 13.105/2015
      — Código de Processo Civil, arts. 319, 824 e seguintes),
      postulando: (i) o valor da consulta (cláusula 5.4.3); (ii) os
      juros (cláusula 5.4.4); (iii) a multa da cláusula 5.4.9; (iv)
      custas processuais e honorários advocatícios (art. 85 do CPC);
      e (v) indenização suplementar, se houver dano excedente (art.
      416, parágrafo único, do Código Civil). Fundamentos: arts. 187,
      389, 395, 397, 406, 407, 408 a 416, 422, 884 e 885 do Código
      Civil; arts. 42 e 52 da Lei n.º 8.078/1990 (Código de Defesa do
      Consumidor), no que couber à cobrança regular de débito;
      orientação reiterada do Superior Tribunal de Justiça de que a
      cláusula penal compensatória independe de prova de prejuízo
      (art. 416 do Código Civil) e de que o art. 884 consagra a
      vedação ao enriquecimento sem causa como cláusula geral; deveres
      anexos da boa-fé objetiva (art. 422 do Código Civil). Quando a
      contestação for fraudulenta, a apuração nas esferas cível e, se
      o caso, criminal (art. 171 do Código Penal) fica a critério das
      autoridades competentes, sem que esta cláusula constitua
      acusação ao(à) paciente. Permanecem assegurados os direitos
      irrenunciáveis do consumidor (cláusula 5.4.1).
      <br />
      <br />
      5.5. <strong>Ciência da ausência de prazo, direito de reembolso
      integral até o início e aceite tácito do prazo da
      consulta.</strong> Ao confirmar o pagamento e contratar a
      consulta, você declara ter ciência prévia e expressa: (i) da
      natureza assíncrona do serviço; (ii) da inexistência de prazo
      máximo para o início efetivo da consulta e para o envio das
      respostas do médico, sem garantia de horário determinado
      (cláusulas 1.4, 1.4.1, 3.6 e 4.1); e (iii) do compromisso de
      manutenção do canal com o médico por 48 (quarenta e oito) horas,
      ressalvadas as hipóteses de encerramento antecipado previstas na
      cláusula 3.5; e (iv) de que a insatisfação com o tempo de
      resposta dentro dessa janela de 48 horas não configura falha do
      serviço nem gera direito a reembolso (cláusulas 3.6 e 5.3),
      permanecendo o médico obrigado a envidar seus melhores esforços
      para responder em prazo razoável.
      <br />
      <br />
      Enquanto a consulta ainda <strong>não tiver sido iniciada</strong>{' '}
      pelo médico responsável — na forma da cláusula 1.4.2 (envio da
      conduta detalhada, de documento médico ou de mensagem do próprio
      médico sobre o caso no chat) — você tem direito a
      cancelar e receber, por arrependimento e independentemente de
      justificativa (art. 49 do Código de Defesa do Consumidor),{' '}
      <strong>reembolso integral a qualquer momento</strong>, desde que
      o faça <strong>antes do início efetivo da consulta</strong> pelos
      canais oficiais do primeiro item da cláusula 5.3: (a) a
      ferramenta/botão de cancelamento com reembolso automático no
      aplicativo (card “Mudou de ideia?” / “Cancelar consulta e receber
      reembolso”); ou (b) o canal de atendimento oficial
      (atendimento@consultamais.app), mediante{' '}
      <strong>verificação completa de identidade</strong> (cláusulas 2.5
      e 2.6). Pedidos
      isolados (mensagem isolada no chat ou e-mail isolado) e
      reclamações em canais externos (Procon, Reclame Aqui e similares)
      não substituem esses canais oficiais nem ensejam, por si sós,
      reembolso automático, nos termos do primeiro item da cláusula
      5.3.
      <br />
      <br />
      <strong>Deixar de exercer o cancelamento com reembolso pelos
      canais oficiais até o início efetivo da consulta</strong>{' '}
      (cláusula 1.4.2) implica, para todos os fins: (i) a{' '}
      <strong>desistência do direito de reembolso por
      arrependimento</strong> ou mera mudança de ideia; e (ii) o{' '}
      <strong>aceite tácito do tempo de espera</strong> até o início
      efetivo daquela consulta, qualquer que tenha sido a sua duração,
      sem prejuízo das hipóteses de vício do serviço e dos
      demais direitos irrenunciáveis previstos no Código de Defesa do
      Consumidor.
      <br />
      <br />
      <strong>Após o início efetivo da consulta</strong> (cláusula
      1.4.2), <strong>não haverá direito automático a reembolso, total
      ou parcial</strong>, se você desistir do atendimento, mudar de
      ideia quanto à contratação, buscar ou optar por serviço médico
      alternativo (presencial ou digital) por iniciativa própria, ou
      deixar de aguardar o tempo de atendimento da modalidade
      assíncrona, <strong>exceto</strong> nas hipóteses expressamente
      previstas na cláusula 5.3. Essa limitação decorre dos custos
      operacionais e financeiros já incorridos pela Plataforma e pelo
      médico com a efetiva prestação do serviço — análise clínica,
      preparação da conduta, emissão de documentos e manutenção da
      disponibilidade profissional — preservados os direitos
      irrenunciáveis previstos no Código de Defesa do Consumidor.
      <br />
      <br />
      5.6. <strong>Promoções e benefícios vinculados a avaliações em
      plataformas públicas de reputação (ex.: Reclame Aqui).</strong> A
      Plataforma pode, a seu exclusivo critério, oferecer descontos,
      cupons ou outras condições promocionais vinculados à avaliação
      voluntária e verídica do atendimento em plataformas públicas de
      reputação (por exemplo, Reclame Aqui). O benefício é concedido em
      contrapartida à avaliação espontânea e de boa-fé mantida pelo
      paciente. <strong>Após o gozo do desconto ou benefício
      promocional</strong>, constitui <strong>atitude de má-fé</strong> —
      em violação aos deveres de lealdade e boa-fé objetiva (arts. 113,
      187 e 422 do Código Civil) — a prática, <strong>sem fundamento
      objetivo</strong>, de qualquer dos seguintes atos:
      <br />
      • <strong>arquivar</strong>, excluir ou ocultar a avaliação que
      fundamentou a concessão do benefício;<br />
      • <strong>reverter</strong>, alterar ou rebaixar a avaliação, a
      nota ou o status de resolução atribuídos;<br />
      • <strong>inserir nova avaliação</strong>, reclamação ou
      manifestação negativa referente ao mesmo atendimento já avaliado
      e beneficiado.
      <br />
      <br />
      Considera-se <strong>fundamento objetivo</strong>, para os fins
      desta cláusula, fato novo, relevante e comprovável, ocorrido após
      a concessão do benefício e diretamente relacionado ao atendimento
      avaliado.
      <br />
      <br />
      5.6.1. <strong>Multa compensatória e suspensão por má-fé
      pós-benefício (cláusula penal).</strong> A prática de qualquer dos
      atos descritos na cláusula 5.6 sujeita o paciente:
      <br />
      • ao pagamento de <strong>multa compensatória equivalente a 1
      (uma) vez o valor do desconto usufruído</strong>, a título de
      cláusula penal compensatória (arts. 408 a 416 do Código Civil),
      cuja exigência, nos termos do art. 416 do Código Civil,{' '}
      <strong>independe de comprovação de prejuízo</strong>;<br />
      • à <strong>suspensão do uso da Plataforma enquanto durar a
      investigação de conduta indevida</strong>, comunicada ao e-mail
      cadastrado — eventual reavaliação ou restabelecimento do acesso
      ficando a critério exclusivo da Plataforma.
      <br />
      <br />
      Fica convencionado, na forma do parágrafo único do art. 416 do
      Código Civil, que a multa vale como <strong>mínimo de
      indenização</strong>, podendo a Plataforma e/ou o médico exigir
      indenização suplementar pelos danos comprovadamente excedentes
      (por exemplo, custos de apuração e danos à reputação da Plataforma
      e do profissional).
      <br />
      <br />
      5.6.2. <strong>Cobrança do valor do desconto usufruído
      (recuperação do benefício promocional Reclame Aqui).</strong>{' '}
      Sem prejuízo da multa e da suspensão previstas na cláusula 5.6.1,
      a prática de qualquer dos atos descritos na cláusula 5.6 —
      inclusive <strong>arquivamento</strong>, exclusão ou ocultação da
      avaliação positiva, <strong>alteração ou rebaixamento da nota</strong>,
      mudança do status de resolução, ou inserção de nova avaliação
      negativa sobre o mesmo atendimento — autoriza a Plataforma a{' '}
      <strong>cobrar o valor do desconto ou benefício promocional
      efetivamente usufruído</strong> (por exemplo, o equivalente a 50%
      do preço da consulta, quando esse for o benefício concedido). Essa
      cobrança poderá ser efetuada, a critério da Plataforma:
      <br />
      • mediante <strong>soma automática ao preço da próxima consulta</strong>{' '}
      contratada pelo paciente na Plataforma, com discriminação na tela
      de pagamento (consulta + pendência); e/ou<br />
      • mediante <strong>link de pagamento avulso</strong> (Pix ou cartão)
      para quitação isolada da pendência, sem liberar nova consulta.
      <br />
      <br />
      O paciente poderá, nas hipóteses e prazos indicados pela Plataforma,
      <strong>regularizar a pendência também pela reativação</strong> da
      avaliação positiva original (incluindo nota e status de resolução)
      nas condições exigidas para a concessão do benefício, caso em que a
      cobrança poderá ser cancelada. A mera existência da pendência{' '}
      <strong>não bloqueia</strong> o acesso ao aplicativo; o valor
      permanece em aberto até quitação, reativação válida ou outra
      solução comunicada pela Plataforma.
    </p>

    <p>
      <strong>6. Privacidade, LGPD e confidencialidade médica</strong>
      <br />
      <br />
      6.1. A Plataforma coleta e trata dados pessoais e dados de saúde,
      que são considerados dados sensíveis. Esses dados incluem: nome,
      idade, sexo, sintomas, histórico clínico, uso de medicamentos,
      hábitos de vida, imagens enviadas, documentos anexados e dados de
      pagamento.
      <br />
      <br />
      6.2. Finalidades principais do uso de dados:
      <br />
      • viabilizar o atendimento médico por chat;<br />
      • manter histórico clínico mínimo necessário durante a
      consulta;<br />
      • gerar documentos médicos e permitir ao paciente recuperá-los
      enquanto a consulta estiver ativa;<br />
      • cumprir obrigações legais, regulatórias e éticas
      (responsabilização profissional, guarda de prontuário dentro de
      prazos legais aplicáveis);
      <br />
      • promover segurança antifraude (ex.: evitar uso de nome falso
      para obter receitas controladas).
      <br />
      <br />
      6.3. Compartilhamento:
      <br />
      • Os dados podem ser acessados pelo(s) médico(s) responsável(is)
      pelo seu atendimento, e eventualmente por profissionais de apoio
      administrativo/tecnológico estritamente necessários para manter o
      serviço funcional e seguro;<br />
      • A Plataforma pode usar fornecedores de tecnologia (hospedagem,
      processamento de mensagens, provedores de pagamento, serviços de
      notificação) que atuam como operadores de dados, sob obrigações
      contratuais de confidencialidade e segurança da informação;
      <br />
      • Não compartilharemos dados identificáveis com terceiros para
      fins de publicidade direcionada.
      <br />
      <br />
      6.4. Segurança:
      <br />
      • Adotamos medidas técnicas e administrativas razoáveis para
      proteger contra acesso não autorizado, vazamento, alteração ou
      destruição indevida dos dados;<br />
      • Ainda assim, o paciente reconhece que nenhum ambiente digital é
      100% isento de risco. Ao prosseguir, você aceita esse risco
      residual típico de serviços digitais.
      <br />
      <br />
      6.5. Sigilo médico:
      <br />
      • O médico está vinculado ao sigilo profissional e ao Código de
      Ética Médica;<br />
      • O conteúdo do chat médico integra o prontuário do paciente,
      podendo ser acessado futuramente pelo próprio paciente, por seus
      responsáveis legais ou por autoridades competentes nos casos
      previstos em lei (por exemplo, ordem judicial).
      <br />
      <br />
      6.6. <strong>Comunicações de marketing e uso de dados de
      contato</strong>
      <br />
      <br />
      6.6.1. Ao aceitar estes Termos, você autoriza expressamente a
      Consulta+ a utilizar seu <strong>número de telefone</strong>{' '}
      (incluindo WhatsApp e SMS) e seu <strong>endereço de
      e-mail</strong>, coletados durante o cadastro ou o atendimento,
      para o envio de comunicações comerciais e de marketing,
      incluindo, mas não se limitando a:
      <br />
      • informações sobre novos serviços, funcionalidades e melhorias
      da Plataforma;<br />
      • ofertas promocionais, descontos e condições especiais para
      consultas e serviços de saúde;<br />
      • conteúdos educativos e informativos sobre saúde, prevenção e
      bem-estar;<br />
      • pesquisas de satisfação e avaliação de qualidade;<br />
      • lembretes de cuidado preventivo e acompanhamento de saúde;<br />
      • comunicados institucionais e informativos relevantes.
      <br />
      <br />
      6.6.2. <strong>Base legal (LGPD):</strong> O tratamento de dados
      de contato (telefone e e-mail) para finalidades de marketing
      fundamenta-se no <strong>consentimento do titular</strong>{' '}
      (art. 7º, inciso I, da Lei nº 13.709/2018 – LGPD), manifestado
      de forma livre, informada e inequívoca ao aceitar estes Termos.
      Para comunicações diretamente relacionadas ao serviço já
      contratado (como informações sobre consultas futuras ou
      melhorias no atendimento), a Plataforma poderá também se
      fundamentar no <strong>legítimo interesse</strong> (art. 7º,
      inciso IX, da LGPD), sempre observando a transparência e a
      razoabilidade da expectativa do titular.
      <br />
      <br />
      6.6.3. <strong>Separação entre dados de saúde e dados de
      contato:</strong> Os dados sensíveis de saúde (sintomas,
      diagnósticos, histórico clínico, medicamentos etc.) <strong>não
      serão utilizados para fins de marketing, segmentação publicitária
      ou direcionamento comercial</strong>. Somente os dados cadastrais
      de contato (telefone e e-mail) serão utilizados para as
      comunicações descritas nesta cláusula.
      <br />
      <br />
      6.6.4. <strong>Direito de revogação (opt-out):</strong> Você
      poderá revogar seu consentimento para o recebimento de
      comunicações de marketing a qualquer momento, sem custo, sem
      justificativa e sem que isso afete a prestação do serviço de
      atendimento médico, por meio de:
      <br />
      • link ou botão de descadastro presente em cada e-mail
      enviado;<br />
      • resposta com a palavra "SAIR" ou equivalente em mensagens
      SMS/WhatsApp;<br />
      • solicitação direta aos canais de suporte indicados na
      Plataforma.
      <br />
      A revogação será processada em até 5 (cinco) dias úteis. Após o
      opt-out, você deixará de receber comunicações de marketing, mas
      poderá continuar recebendo comunicações estritamente necessárias
      à prestação do serviço (como confirmações de consulta,
      notificações operacionais e avisos de segurança), que possuem
      base legal autônoma.
      <br />
      <br />
      6.6.5. <strong>Frequência e boas práticas:</strong> A Plataforma
      compromete-se a manter uma frequência razoável de envio,
      evitando comunicações excessivas. Não serão realizadas ligações
      telefônicas para fins de marketing ativo sem prévio agendamento
      ou autorização específica do titular.
      <br />
      <br />
      6.6.6. <strong>Não compartilhamento para marketing de
      terceiros:</strong> Seus dados de telefone e e-mail <strong>não
      serão vendidos, alugados ou compartilhados com terceiros para
      fins de marketing próprio desses terceiros</strong>. O uso é
      restrito às comunicações da própria Consulta+ e de seus
      serviços.
      <br />
      <br />
      6.6.7. <strong>Registro e comprovação de consentimento:</strong>{' '}
      A Plataforma manterá registro do consentimento concedido
      (incluindo data, hora, IP e versão dos Termos aceitos), como
      evidência de conformidade com a LGPD e para fins de eventual
      auditoria pela Autoridade Nacional de Proteção de Dados (ANPD).
    </p>

    <p>
      <strong>7. Seus direitos sobre dados pessoais</strong>
      <br />
      <br />
      7.1. Você pode, nos termos da Lei Geral de Proteção de Dados
      (LGPD), solicitar:
      <br />
      • confirmação de que tratamos seus dados;<br />
      • acesso a dados pessoais seus que mantemos;<br />
      • correção de dados incompletos, inexatos ou desatualizados;<br />
      • informação sobre compartilhamento com terceiros autorizados;<br />
      • eliminação de dados pessoais desnecessários ou tratados em
      desconformidade (observados prazos legais e obrigações de guarda
      médica e sanitária);
      <br />
      • revogação de consentimentos específicos que sejam revogáveis em
      lei.
      <br />
      <br />
      7.2. Limitações: mesmo após solicitação de exclusão, certos dados
      clínicos e registros de atendimento podem precisar ser mantidos
      pelo período exigido para guarda de prontuário médico, defesa
      profissional e cumprimento de normas sanitárias, éticas e legais
      (incluindo prazos mínimos aplicáveis à guarda do prontuário, nos
      termos da legislação vigente, como a Lei nº 13.787/2018, quando
      couber). Esse dever de guarda é legítimo e não viola a LGPD.
      <br />
      <br />
      7.3. <strong>Como solicitar:</strong> pedidos para exercer os
      direitos previstos na LGPD (item 7.1) podem ser enviados para{' '}
      <a
        href="mailto:atendimento@consultamais.org?subject=Pedido%20LGPD%20%E2%80%93%20dados%20pessoais"
      >
        atendimento@consultamais.org
      </a>
      {' '}(Encarregado / Diretor Técnico), com assunto e informações suficientes para localizar o titular
      com segurança. A Plataforma poderá solicitar comprovação de
      identidade antes de conceder acesso, retificação ou exclusão,
      de forma compatível com o art. 19 da LGPD e com a proteção de
      dados de saúde. Nessa hipótese, é dever do paciente comprovar a
      identidade mediante o envio de documento oficial, nos termos das
      cláusulas 2.5 e 2.6.
      <br />
      <br />
      7.4. <strong>Prazos:</strong> observado o art. 19 da LGPD, a
      confirmação de tratamento ou o acesso em forma simplificada será
      prestada de forma imediata, sempre que possível; já a declaração
      clara e completa sobre a origem dos dados, critérios e finalidades
      poderá levar até 15 (quinze) dias, contados do requerimento,
      prorrogável por igual período mediante justificativa. Demais
      solicitações (correção, revogação de consentimento, eliminação nos
      limites do item 7.2) serão analisadas e respondidas no menor prazo
      razoável, conforme a complexidade e as obrigações legais aplicáveis
      a cada caso.
    </p>

    <p>
      <strong>8. Uso de dados clínicos anonimizados e agregados</strong>
      <br />
      <br />
      8.1. Podemos gerar conjuntos estatísticos e anonimizados (sem
      identificação pessoal direta ou indireta razoável) a partir de
      dados clínicos coletivos, para fins como:
      <br />
      • análise interna de qualidade e segurança;<br />
      • elaboração de indicadores de saúde;<br />
      • pesquisas clínicas e epidemiológicas;<br />
      • parcerias acadêmicas ou farmacêuticas para estudos de efetividade
      e segurança de condutas;<br />
      • publicação de estudos e artigos científicos em periódicos
      acadêmicos, congressos e eventos científicos, sempre com dados
      agregados e anonimizados, sem possibilidade de identificação
      individual;<br />
      • análise de padrões de prescrição e uso de medicamentos por
      categoria diagnóstica, visando melhoria de protocolos
      assistenciais e contribuição ao conhecimento científico.
      <br />
      <br />
      8.2. Esses conjuntos são agregados e anonimizados. Não serão
      fornecidos dados pessoais identificáveis a terceiros para fins
      comerciais sem consentimento específico, destacado e informado.
      <br />
      <br />
      8.3. Os dados anonimizados utilizados em pesquisas não permitem,
      por meios técnicos razoáveis, a reidentificação do titular. A
      anonimização inclui: substituição de identificadores por códigos
      irreversíveis, conversão de idade em faixa etária, supressão de
      datas exatas (mantendo apenas mês e ano) e exclusão de qualquer
      dado que possa individualizar o paciente.
      <br />
      <br />
      8.4. Você pode solicitar que seus dados não sejam incluídos em
      análises futuras agregadas, desde que tecnicamente viável e sem
      afetar análises já concluídas ou relatórios já publicados em
      formato estatístico.
    </p>

    <p>
      <strong>9. Uso de Inteligência Artificial (IA) e responsabilidade clínica</strong>
      <br />
      <br />
      9.1. A Plataforma utiliza ferramentas de Inteligência Artificial
      (IA) como apoio tecnológico em diversas etapas do atendimento, em
      conformidade com a <strong>Resolução CFM nº 2.454, de 11 de
      fevereiro de 2026</strong>, que normatiza o uso de IA na medicina.
      Ao prosseguir, você declara estar ciente e concordar que a IA pode
      ser empregada para:
      <br />
      <br />
      (a) <strong>Triagem inicial (assistente “Alice”)</strong> — a IA
      conduz a etapa de triagem (anamnese guiada), coletando e organizando
      as informações sobre seus sintomas, histórico e queixa principal,
      de modo a agilizar e estruturar o atendimento antes da avaliação
      médica;
      <br />
      <br />
      (b) <strong>Perguntas adicionais</strong> — durante o atendimento,
      a IA pode formular perguntas complementares para aprofundar a
      compreensão do seu quadro clínico, garantindo que o médico receba
      informações mais completas para a tomada de decisão;
      <br />
      <br />
      (c) <strong>Formulação de texto de conduta</strong> — a IA pode
      redigir rascunhos de orientações, condutas e recomendações
      clínicas.
      <br />
      <br />
      (d) <strong>Preenchimento de sugestões de atestado e receita</strong>
      — a IA pode preencher previamente campos e sugestões em modelos
      de atestados médicos e receitas (incluindo medicamentos, posologia,
      duração de afastamento e CID), com base nas informações clínicas
      coletadas. Essas sugestões são apenas rascunhos, sujeitos a
      edição do supervisor médico, com assinatura digital ICP-Brasil;
      <br />
      <br />
      (e) <strong>Interpretação clínica de imagens e exames</strong> —
      o médico pode acionar uma análise auxiliar por IA sobre fotos
      clínicas (garganta, olho, lesão de pele) ou laudos em PDF anexados
      ao chat. O resultado é apenas apoio diagnóstico; a decisão clínica
      é sempre do médico;
      <br />
      <br />
      (f) <strong>Suporte operacional (assistente “Sofia”)</strong> —
      chatbot de FAQ e suporte pré e pós-consulta. <strong>Sofia não tem
      acesso a prontuário, não conduz atendimento clínico</strong> e
      transfere automaticamente para a equipe humana em qualquer questão
      clínica.
      <br />
      <br />
      9.1.1. <strong>Classificação de risco (Anexo II da Resolução CFM
      2.454/2026):</strong> a Sofia é classificada como{' '}
      <strong>baixo risco</strong> (apoio administrativo, sem influência
      decisória clínica). Os demais componentes (Alice/triagem, conduta,
      sugestões clínicas, interpretação de imagem e detecção de alertas)
      são classificados como <strong>médio risco</strong> — apoiam
      decisões clínicas mas não as executam de forma autônoma; a supervisão
      humana obrigatória é o controle principal. Detalhes públicos em{' '}
      <a
        href="/transparencia-ia"
        target="_blank"
        rel="noopener noreferrer"
      >
        consultamais.app/transparencia-ia
      </a>
      .
      <br />
      <br />
      9.2. <strong>A IA é ferramenta auxiliar, não substituta do
      médico.</strong> Todo conteúdo gerado por IA — incluindo triagem,
      perguntas, textos de conduta, sugestões de atestado e receita,
      interpretação de imagens — constitui apoio técnico e NÃO representa,
      por si só, diagnóstico definitivo, prescrição final, afastamento
      garantido ou autorização para automedicação. A IA existe para
      agilizar e qualificar o processo, mas a decisão clínica é sempre
      humana.
      <br />
      <br />
      9.3. A conduta final (por exemplo: “você deve ir à emergência
      agora”, “receita emitida”, “atestado de X dias justificado por Y”,
      “CID tal será informado/não será informado”, “seguimento ambulatorial
      em 48h”) é de responsabilidade exclusiva do médico humano, sob
      cuja supervisão toda informação é emitida. O paciente reconhece
      que não deve basear decisões clínicas importantes apenas em
      mensagens automáticas sem essa supervisão médica.
      <br />
      <br />
      9.4. Ao clicar em "Aceitar Termos", você manifesta concordância
      expressa com o uso de ferramentas de Inteligência Artificial nas
      etapas descritas em 9.1, ciente de que a validação final é sempre
      realizada por médico humano habilitado e identificado{' '}
      <strong>(Dr. Guilherme Esmanhotto — CRM 23.706/DF)</strong>.
      <br />
      <br />
      9.4.1. <strong>Recusa do uso de IA.</strong> A IA integra o método
      de atendimento da Plataforma, que não oferece consulta sem o seu
      uso. <strong>Se você não concorda com o uso de IA descrito na
      cláusula 9.1, não aceite estes Termos e procure atendimento
      presencial ou outra plataforma.</strong> Em situação de urgência ou
      emergência, procure imediatamente um pronto-socorro ou ligue para o
      SAMU (192).
      <br />
      <br />
      9.4.2. <strong>Recusa depois do aceite.</strong> Você pode recusar o
      uso de IA a qualquer momento, pelo chat médico ou pelo e-mail
      atendimento@consultamais.app. Como a Plataforma não oferece
      consulta sem IA, a recusa leva ao encerramento do atendimento pelo
      médico responsável, com orientação para que você prossiga em
      atendimento presencial ou em outra plataforma, e fica registrada no
      prontuário. A recusa não dá direito a reembolso além do previsto
      nas cláusulas 5.3 e 5.5, e os documentos já emitidos permanecem
      válidos.
      <br />
      <br />
      9.5. <strong>Dever expresso de revisão da triagem e comunicação
      imediata de imprecisões.</strong> O paciente declara estar ciente
      de que, por se tratar de processamento parcialmente automatizado
      (incluindo coleta e organização de dados por IA), podem ocorrer{' '}
      <strong>falhas, omissões, erros de interpretação ou imprecisões
      </strong> nas informações coletadas durante a triagem (sintomas,
      histórico clínico, medicamentos em uso, alergias, posologias,
      tempo de evolução, dados de identificação e demais dados
      clínicos). Ao aceitar estes Termos, o paciente{' '}
      <strong>compromete-se expressamente a</strong>:
      <br />
      (a) <strong>revisar com atenção</strong>, ao final da triagem e
      antes do início do atendimento médico, todas as informações
      processadas e exibidas pela Plataforma;<br />
      (b) reconhecer que essa revisão é parte integrante e indispensável
      do seu próprio cuidado em saúde, dado que decisões clínicas
      subsequentes (incluindo orientações, prescrições e emissão de
      atestados/receitas) podem se basear nesses dados;<br />
      (c) <strong>avisar imediatamente</strong> qualquer imprecisão,
      divergência, informação incorreta ou dado faltante diretamente
      ao médico responsável, <strong>por meio do chat médico
      (ConsultaChat) dentro do próprio aplicativo</strong>, antes ou
      durante o atendimento.
      <br />
      <br />
      9.5.1. O paciente reconhece e aceita que o descumprimento desse
      dever de revisão e comunicação — em especial a omissão de
      correção de informações que ele teve oportunidade de revisar —{' '}
      <strong>afasta a responsabilidade da Plataforma e do médico
      </strong> por condutas clínicas que tenham se baseado, total ou
      parcialmente, em dados imprecisos não corrigidos pelo paciente
      quando lhe foi expressamente solicitado revisá-los.
      <br />
      <br />
      9.6. <strong>Direitos do paciente quanto à IA</strong> (Resolução
      CFM 2.454/2026 e LGPD):
      <br />
      (a) <strong>Ser informado</strong> de forma clara e acessível
      quando a IA é usada como apoio relevante no seu cuidado (Art. 5°§1°
      e Art. 11) — esta cláusula 9 e a página{' '}
      <a
        href="/transparencia-ia"
        target="_blank"
        rel="noopener noreferrer"
      >
        consultamais.app/transparencia-ia
      </a>
      {' '}atendem a esse direito;<br />
      (b) <strong>Receber explicação</strong> compreensível sobre como
      uma sugestão de IA influenciou sua conduta;<br />
      (c) <strong>Supervisão humana obrigatória</strong> de toda decisão
      derivada de IA (Anexo I, XX da Resolução — contestabilidade);<br />
      (d) <strong>Solicitar segunda opinião</strong> a qualquer momento
      (Art. 10, II);<br />
      (e) <strong>Contestar</strong> uma conduta apoiada por IA enviando
      e-mail para{' '}
      <a
        href="mailto:atendimento@consultamais.org?subject=%5BCONTESTABILIDADE-IA%5D"
      >
        atendimento@consultamais.org
      </a>
      {' '}(Diretor Técnico) com assunto <code>[CONTESTABILIDADE-IA]</code>; a resposta
      ocorre em até 15 dias corridos (LGPD art. 19 §1°);<br />
      (f) <strong>Recusar o uso de IA</strong> (Art. 5°§3°), nos termos
      das cláusulas 9.4.1 e 9.4.2.
      <br />
      <br />
      9.7. <strong>Registro do uso de IA no prontuário (Art. 4°V).
      </strong> O uso de ferramentas de IA neste atendimento, incluindo
      a lista das ferramentas efetivamente acionadas, é registrado de
      forma estruturada no prontuário eletrônico do paciente, podendo
      ser consultado por auditoria de órgão competente.
      <br />
      <br />
      9.8. <strong>Tratamento de dados pelo provedor de IA.</strong>
      Os dados clínicos enviados ao modelo de IA{' '}
      <strong>não são utilizados para treinamento</strong> de modelos
      pelo provedor (política padrão da OpenAI desde março/2023). A
      Plataforma <strong>não envia dados de identificação</strong> do
      paciente (nome completo, CPF, e-mail, telefone, identificadores
      internos) para o modelo. Detalhes técnicos em{' '}
      <a
        href="/transparencia-ia"
        target="_blank"
        rel="noopener noreferrer"
      >
        consultamais.app/transparencia-ia
      </a>
      .
    </p>

    <p>
      <strong>10. Atestados, CID, afastamento e limitações legais</strong>
      <br />
      <br />
      10.1. O paciente pode informar preferências quanto a:
      <br />
      • necessidade de atestado de afastamento laboral ou escolar;<br />
      • duração desejada desse afastamento;<br />
      • inclusão (ou não) de CID no documento;<br />
      • tipo de queixa principal que deseja que conste.
      <br />
      <br />
      10.2. Essa preferência NÃO cria direito adquirido. Atestado e
      CID são instrumentos médicos e legais. A decisão final sobre:
      <br />
      • se há fundamento clínico suficiente;<br />
      • qual período é justificável;<br />
      • se constarão detalhes diagnósticos;
      <br />
      pertence exclusivamente ao médico, que deve seguir ética médica,
      normas trabalhistas, previdenciárias e regulamentos do CFM.
      <br />
      <br />
      10.3. O médico pode recusar afastamento prolongado, indeferir
      CID específico ou orientar retorno/reeavaliação presencial. Isso
      não configura falha do serviço nem gera automaticamente direito a
      reembolso.
      <br />
      <br />
      10.3.1. <strong>Caráter preliminar do período de afastamento
      indicado no texto inicial de conduta.</strong> A eventual menção a
      período de afastamento no texto inicial de conduta enviado ao
      paciente tem caráter preliminar e não constitui compromisso
      definitivo de emissão de atestado com aquela duração. Esse período
      pode ser revisado pelo médico ao longo da consulta — inclusive
      ampliado, reduzido ou indeferido — a partir de critérios técnicos
      e clínicos, tais como informações complementares prestadas pelo
      paciente, documentos e imagens enviados e a evolução do quadro
      relatado. Prevalece, para todos os efeitos, exclusivamente o
      período que constar do atestado médico efetivamente emitido e
      assinado digitalmente pelo médico responsável.
      <br />
      <br />
      10.4. É proibido alterar, rasurar, vender, revender, compartilhar
      publicamente ou ceder a terceiros (ex.: “grupo de Telegram”,
      “template de atestado”) qualquer documento médico emitido para
      você, bem como utilizá-lo como se fosse de outra pessoa.
      Tentativas de fraude poderão ser levadas à autoridade competente.
      <br />
      <br />
      10.5. <strong>Validação da assinatura digital e etapas em{' '}
      <a
        href="/autenticar"
        target="_blank"
        rel="noopener noreferrer"
      >
        consultamais.app/autenticar
      </a>
      .</strong> O paciente declara ter ciência de que atestados e demais
      documentos médicos emitidos digitalmente pela Plataforma possuem
      assinatura digital com certificado ICP-Brasil e que empregadores,
      escolas, seguradoras ou outros terceiros podem exigir a comprovação
      de autenticidade no portal oficial do ITI (validar.iti.gov.br),
      conforme o passo a passo publicado e atualizado em{' '}
      <a
        href="/autenticar"
        target="_blank"
        rel="noopener noreferrer"
      >
        consultamais.app/autenticar
      </a>
      .
      <br />
      <br />
      Ao aceitar estes Termos, o paciente <strong>compromete-se
      expressamente a</strong>:
      <br />
      (a) <strong>conhecer e cumprir</strong> as etapas indicadas em{' '}
      consultamais.app/autenticar, em especial: utilizar o{' '}
      <strong>PDF original</strong> recebido da Plataforma; realizar a
      validação pelo <strong>upload do arquivo PDF</strong> no portal
      validar.iti.gov.br; e <strong>não</strong> validar por impressão,
      digitalização, conversão para imagem ou por aplicativos que alterem
      o arquivo (como conversão automática de anexos em apps de e-mail);<br />
      (b) <strong>informar e orientar a empresa, o empregador, o RH, a
      escola ou qualquer terceiro</strong> que exija comprovação do
      atestado quanto a essas etapas, fornecendo o PDF original e
      indicando o procedimento correto de validação descrito em{' '}
      consultamais.app/autenticar;<br />
      (c) <strong>comunicar prontamente à Plataforma</strong> (por meio
      de{' '}
      <a href="mailto:atendimento@consultamais.app?subject=Valida%C3%A7%C3%A3o%20de%20atestado">
        atendimento@consultamais.app
      </a>
      ) caso a validação falhe <strong>após</strong> o cumprimento das
      etapas de consultamais.app/autenticar, anexando o PDF original, o
      print da mensagem de erro exibida no ITI e a confirmação de que a
      tentativa foi feita pelo upload do PDF, conforme descrito na página
      de autenticação.
      <br />
      <br />
      O paciente reconhece e aceita que <strong>falhas de validação
      decorrentes do descumprimento</strong> das etapas de{' '}
      consultamais.app/autenticar — incluindo, mas não se limitando a:
      uso de arquivo alterado, impresso e escaneado, convertido em imagem,
      obtido por app que modifique o PDF, ou tentativa exclusiva por QR
      Code quando a página recomenda upload — <strong>não constituem
      falha do serviço de teleconsulta</strong> e <strong>não geram
      responsabilidade da Plataforma</strong> (nem do médico) por recusa,
      dúvida, suspensão, desconto ou penalidade imposta por terceiros com
      base em assinatura considerada inválida.
      <br />
      <br />
      O paciente <strong>responsabiliza-se integralmente</strong> por
      cumprir as etapas de validação, por preservar o PDF original, por
      orientar corretamente a empresa ou terceiros interessados e por
      comunicar à Plataforma, nos termos acima, qualquer dificuldade
      persistente após cumprimento correto dessas etapas. A Plataforma não
      garante funcionamento ininterrupto do portal do ITI nem aceitação
      automática por terceiros, mas disponibiliza o guia em{' '}
      consultamais.app/autenticar e o canal atendimento@consultamais.app
      para apoio técnico quando o paciente seguir o procedimento indicado.
    </p>

    <p>
      <strong>11. Propriedade intelectual e uso correto da
      Plataforma</strong>
      <br />
      <br />
      11.1. Todo o design, interface, fluxos, textos, layout, código,
      ícones, imagens, elementos visuais e lógicos da Plataforma são
      de titularidade da Consulta+ ou licenciados para nós, protegidos
      por direitos autorais e marcas.
      <br />
      <br />
      11.2. Você recebe apenas uma licença pessoal, limitada,
      revogável, não exclusiva e intransferível para usar a Plataforma
      com a finalidade única de receber atendimento médico dentro dos
      termos aqui descritos.
      <br />
      <br />
      11.3. É expressamente vedado:
      <br />
      • copiar, reproduzir, retransmitir, republicar ou explorar
      comercialmente qualquer parte da Plataforma sem autorização
      expressa por escrito;<br />
      • utilizar engenharia reversa, scraping agressivo ou automações
      não autorizadas que afetem estabilidade, segurança ou privacidade
      do sistema;<br />
      • utilizar o chat para assediar, ameaçar, difamar, discriminar,
      intimidar ou tentar coagir o médico a emitir documentos sem base
      clínica.
    </p>

    <p>
      <strong>12. Limitação de responsabilidade</strong>
      <br />
      <br />
      12.1. A Plataforma será fornecida “no estado em que se encontra”,
      dentro do que é tecnicamente razoável em serviços digitais de
      saúde remota. Esforços de disponibilidade, continuidade e
      segurança são adotados, mas não há garantia de funcionamento
      ininterrupto, livre de falhas técnicas, atrasos de rede,
      indisponibilidade temporária de profissionais ou limitações
      impostas por fornecedores externos (internet, operadora móvel,
      loja de aplicativos, gateway de pagamento etc.).
      <br />
      <br />
      12.2. Nem a Plataforma nem o médico assumem responsabilidade
      por:
      <br />
      • uso indevido das orientações médicas fornecidas, inclusive se o
      paciente descumprir recomendações de procurar atendimento
      presencial imediato;<br />
      • decisão do paciente de ignorar sinais de alarme e permanecer
      exclusivamente em casa, apesar de ter sido orientado a buscar
      emergência;<br />
      • condutas de terceiros sobre aceitação ou não de atestados,
      receitas e laudos (por exemplo, recusas de farmácias, RH de
      empresa ou seguradora);<br />
      • falha na validação da assinatura digital de atestado ou outro
      documento emitido pela Plataforma quando decorrente do
      descumprimento, pelo paciente ou por terceiros por ele orientados,
      das etapas de consultamais.app/autenticar (cláusula 10.5).
      <br />
      <br />
      12.3. Em nenhuma hipótese a Plataforma responderá, de forma
      automática, por danos indiretos, consequenciais, lucros cessantes
      ou perdas de oportunidade relacionadas ao uso ou à impossibilidade
      de uso do serviço, exceto na medida obrigatória pela legislação
      de defesa do consumidor aplicável e após apuração específica de
      culpa.
      <br />
      <br />
      12.4. <strong>Assunção integral de responsabilidade por indução
      do médico a erro e por informação imprecisa ou falsa.</strong> O
      paciente reconhece que a avaliação médica à distância se apoia,
      de modo essencial, na veracidade, na exatidão e na completude das
      informações, imagens e documentos que ele próprio fornece. Por
      essa razão, o paciente <strong>assume, de forma pessoal, integral
      e exclusiva, a responsabilidade por todas as consequências —
      médicas, à sua própria saúde e jurídicas</strong> (cíveis,
      administrativas e, conforme o caso, penais) — decorrentes de
      qualquer conduta sua que induza ou possa induzir o médico a erro,
      incluindo, em rol exemplificativo:
      <br />
      • o fornecimento de informação imprecisa, incompleta, enganosa ou
      falsa durante a triagem ou a consulta;<br />
      • a omissão de dados clínicos relevantes (como alergias, doenças
      preexistentes, gestação ou amamentação e medicamentos em uso);<br />
      • a simulação, o exagero ou a manipulação de sintomas;<br />
      • o envio de imagens, documentos, receitas ou fotos de
      medicamentos que não correspondam à sua realidade clínica atual
      (cláusulas 2.3, 2.4 e 3.10 a 3.10.3).
      <br />
      <br />
      O paciente declara estar ciente de que orientações, prescrições e
      documentos médicos emitidos a partir de dados inverídicos ou
      imprecisos podem se revelar clinicamente inadequados e{' '}
      <strong>causar dano à sua própria saúde</strong>, sem que tal
      resultado possa ser atribuído a falha do serviço ou a erro do
      médico, que terá agido de boa-fé com base nas informações
      prestadas. Nessas hipóteses, ficam afastadas, nos limites da lei,
      a responsabilidade da Plataforma e a do médico, assegurados a
      ambos o direito de regresso contra o paciente pelos prejuízos que
      vierem a suportar perante terceiros e a adoção das demais medidas
      previstas nestes Termos e na legislação aplicável.
    </p>

    <p>
      <strong>13. Atualizações destes Termos</strong>
      <br />
      <br />
      13.1. Estes Termos podem ser atualizados periodicamente para
      refletir ajustes técnicos, requisitos legais, mudanças
      operacionais ou novas funcionalidades.
      <br />
      <br />
      13.2. A versão vigente será disponibilizada na própria
      Plataforma. O uso continuado após alterações significa aceitação
      das versões mais recentes.
    </p>

    <p>
      <strong>14. Foro, legislação e solução de conflitos</strong>
      <br />
      <br />
      14.1. Estes Termos são regidos pelas leis brasileiras, incluindo
      o Código de Defesa do Consumidor, a legislação sanitária e
      regulatória aplicável à prática médica e à telemedicina, e a Lei
      Geral de Proteção de Dados Pessoais (LGPD).
      <br />
      <br />
      14.2. Em caso de conflito, a Plataforma buscará sempre que
      possível uma solução amigável com o paciente, valorizando o
      diálogo e a boa-fé. Persistindo o conflito, fica eleito o foro da
      sede administrativa da Plataforma, salvo se o Código de Defesa do
      Consumidor determinar, em benefício do consumidor, foro diverso.
    </p>

    <p>
      <strong>15. Contato do paciente e canal de suporte</strong>
      <br />
      <br />
      15.1. Em caso de dúvida sobre estes Termos, privacidade,
      cobrança, funcionamento ou dificuldade técnica, você pode
      solicitar contato em nossos canais indicados na Plataforma
      (inclusive WhatsApp oficial). Para pedidos relativos a dados
      pessoais nos termos da LGPD, utilize{' '}
      <a href="mailto:atendimento@consultamais.org?subject=Contato%20%E2%80%93%20LGPD">
        atendimento@consultamais.org
      </a>
      {' '}(Encarregado / Diretor Técnico).
      <br />
      <br />
      15.2. Esses canais não devem ser usados para emergências médicas
      agudas. Emergência deve ser encaminhada imediatamente a um pronto
      atendimento presencial.
    </p>

    <p>
      <strong>16. Declaração final de consentimento informado
      (TCLE)</strong>
      <br />
      <br />
      Ao prosseguir:
      <br />
      • Você confirma que leu e compreendeu estes Termos;<br />
      • Você concorda com a natureza do atendimento remoto, ciente das
      limitações inerentes à ausência de exame físico presencial;<br />
      • Você compreende que o tempo de resposta pode não ser imediato e
      que casos urgentes exigem pronto atendimento físico;<br />
      • Você autoriza o tratamento dos seus dados pessoais e dados
      sensíveis de saúde para fins de prestação do serviço, guarda
      clínica, segurança e cumprimento de obrigações legais;<br />
      • <strong>Você consente expressamente com o uso do seu número de
      telefone (incluindo WhatsApp e SMS) e endereço de e-mail para o
      recebimento de comunicações de marketing da Consulta+, conforme
      descrito na cláusula 6.6, ciente de que poderá revogar esse
      consentimento a qualquer momento, sem custo e sem prejuízo à
      prestação do serviço;</strong><br />
      • Você reconhece que eventuais documentos médicos só serão
      emitidos se houver indicação clínica e autonomia técnica do
      médico;<br />
      • <strong>Você concorda expressamente com o uso de ferramentas de
      Inteligência Artificial (IA) — em conformidade com a Resolução CFM
      nº 2.454/2026 — para triagem (Alice), perguntas adicionais,
      formulação de textos de conduta sob supervisão médica,
      preenchimento de sugestões de atestado e receita, interpretação
      clínica de imagens/exames e suporte operacional (Sofia), conforme
      descrito na cláusula 9, ciente de que a conduta clínica é
      supervisionada pelo médico responsável (Dr. Guilherme Esmanhotto — CRM
      23.706/DF), e de que a Plataforma não oferece consulta sem IA: se
      você não concorda com esse uso, não deve aceitar estes Termos, e sim
      procurar atendimento presencial ou outra plataforma (cláusula
      9.4.1);</strong><br />
      • <strong>Você se compromete expressamente a revisar, com
      atenção, todas as informações processadas durante a triagem,
      entendendo que pode haver falhas no processamento automatizado,
      e a avisar imediatamente o médico responsável, pelo chat médico
      dentro do próprio aplicativo, sobre qualquer imprecisão ou dado
      incorreto identificado, nos termos da cláusula 9.5;</strong><br />
      • <strong>Você se compromete a ler atentamente as orientações
      médicas enviadas por escrito, com especial atenção a avisos de
      busca de pronto atendimento, atendimento presencial prioritário,
      sinais de gravidade e demais alertas clínicos, e a buscar pronto
      atendimento presencial quando orientado para tal pelo médico
      (cláusula 2.3);</strong><br />
      • <strong>Você concorda em agir com respeito, decoro, urbanidade
      e boa-fé, sem fraude e sem uso abusivo, ciente de que ofensas,
      xingamentos, assédio, ameaças ou conduta indigna ao médico ou à
      equipe de apoio podem ensejar encerramento imediato sem direito a
      reembolso, suspensão de acesso à Plataforma e multa compensatória
      de 1 (uma) vez o valor pago pela consulta (cláusulas 4.4, 4.5
      e 4.5.1);</strong><br />
      • <strong>Você declara ciência de que não há prazo máximo para o
      início efetivo da consulta nem para as respostas do médico
      (cláusulas 1.4, 1.4.1, 3.6 e 4.1) e concorda que, até o início
      efetivo da consulta pelo médico — assim considerado o envio da
      conduta detalhada (hipótese diagnóstica principal, orientações
      gerais e sinais de alerta), de documento médico ou de mensagem do
      próprio médico sobre o caso no chat (cláusula 1.4.2) —, pode
      solicitar, por arrependimento e independentemente
      de justificativa, reembolso integral a qualquer momento pelos
      canais oficiais
      (ferramenta/botão de cancelamento com reembolso automático no
      aplicativo, ou canal de atendimento oficial mediante verificação
      completa de identidade, com o dever de enviar documento oficial
      quando pedido — cláusulas 2.5 e 2.6); que pedidos isolados e
      reclamações em
      canais externos não substituem esses canais oficiais nem ensejam,
      por si sós, reembolso automático (primeiro item da cláusula 5.3);
      que deixar de exercer o cancelamento pelos canais oficiais até o
      início efetivo implica desistência do direito de reembolso por
      arrependimento e aceite tácito do tempo de espera daquela
      consulta; e que, após o início efetivo, desistência, mudança de
      ideia ou busca de serviço médico alternativo não geram direito a
      reembolso automático, salvo hipóteses da cláusula 5.3 (cláusula
      5.5);</strong><br />
      • <strong>Você declara estar ciente de que a Plataforma oferece
      reembolso automático pelos canais oficiais antes do início
      efetivo da consulta; de que 1 (uma) contestação ou chargeback
      abusivo junto ao banco ou à operadora, após a prestação ou a
      disponibilização do serviço, autoriza o bloqueio de novas
      consultas até a quitação do valor da consulta contestada,
      acrescido de juros simples de 1% (um por cento) ao mês, pro rata
      die; de que o chargeback indevido após a prestação do serviço
      sujeita o paciente a multa compensatória de 1 (uma) vez o
      valor da consulta (cláusula 5.4.9), exigível em especial se
      houver notificação extrajudicial ou cobrança judicial (cláusula
      5.4.10); e de que a regularização pode ser feita na própria
      Plataforma via PIX, com desbloqueio automático na confirmação
      (cláusulas 5.4 a 5.4.10);</strong><br />
      • <strong>Você declara ciência das etapas de validação de
      atestados e documentos digitais publicadas em
      consultamais.app/autenticar, compromete-se a cumpri-las, a
      orientar a empresa ou terceiros que exijam comprovação e a informar
      a Plataforma em caso de falha persistente após cumprimento correto
      dessas etapas, responsabilizando-se integralmente por falhas de
      validação decorrentes do seu descumprimento (cláusula 10.5);</strong><br />
      • <strong>Você declara estar ciente de que a Plataforma se
      compromete a manter o canal direto com o médico aberto por 48
      (quarenta e oito) horas consecutivas — salvo encerramento por
      infração a estes Termos, finalização pelo próprio paciente ou
      encerramento a seu pedido —, sem compromisso de resposta em prazo
      máximo dentro desse intervalo, por ser a modalidade assíncrona por
      chat justamente o que viabiliza um canal aberto por período tão
      longo; de que o médico se compromete a envidar seus melhores
      esforços para responder em prazo razoável, observada a
      necessidade de revisar o caso e elaborar respostas adequadas, e
      de que a insatisfação com o tempo de resposta dentro das 48 horas
      não gera direito a reembolso (cláusulas 3.6 e 5.3); e de que a
      teleconsulta assíncrona, embora possa oferecer
      apoio, não é adequada ao manejo completo de situações de urgência
      ou emergência, casos em que você deve buscar pronto atendimento
      presencial imediatamente (cláusulas 3.5, 3.6 e 3.6.1);</strong><br />
      • <strong>Você declara estar ciente de que a recusa em enviar imagem
      considerada pelo médico fundamental ao diagnóstico pode levar à
      recusa de emissão dos documentos médicos correspondentes, em razão
      da incerteza diagnóstica (cláusula 3.7);</strong><br />
      • <strong>Você declara estar ciente de que pedidos repetitivos sobre
      assunto já respondido de forma clara e objetiva podem ensejar o
      encerramento da consulta, por perturbação dos demais atendimentos
      (cláusula 4.6);</strong><br />
      • <strong>Você declara estar ciente de que a consulta é prestada
      exclusivamente por chat (troca de mensagens de texto, com
      complementação por imagens), obriga-se a enviar as imagens
      solicitadas pelo médico e reconhece que a incapacidade técnica ou de
      outra natureza de enviá-las pode levar o médico a declarar
      informações insuficientes e recomendar atendimento presencial, sem
      direito a reembolso (cláusulas 3.2 e 3.8);</strong><br />
      • <strong>Você declara estar ciente de que o médico não se obriga a
      emitir receitas, exames, encaminhamentos ou atestados em formato
      exato solicitado pelo paciente, mas sim em formato usual e comumente
      aceito por farmácias, laboratórios e clínicas diagnósticas, e de que
      a inconformidade com esse formato não dá direito a reembolso
      (cláusula 3.9);</strong><br />
      • <strong>Você declara que toda imagem enviada durante a triagem ou
      a consulta é própria, autêntica e atual — retratando você (ou a
      pessoa assistida) e o seu quadro clínico real no momento presente —,
      compromete-se a enviar somente fotos atuais do seu quadro e está
      ciente de que o envio de imagem obtida na internet, de domínio
      público, de terceiros, alterada, ou de foto antiga apresentada como
      atual (inclusive quando identificada pelos metadados do arquivo)
      constitui fraude e pode ensejar encerramento imediato da consulta
      sem reembolso, invalidação dos documentos emitidos, suspensão ou
      bloqueio de acesso e comunicação às autoridades — observado o
      procedimento de apuração, que exige <em>antes de qualquer
      medida</em> que você seja questionado no próprio chat e confirme a
      autoria e a atualidade da imagem, aplica a suspensão por prazo
      indeterminado e abre 5 dias úteis facultativos para esclarecimentos
      livres (cláusulas 3.10, 3.10.1, 3.10.2, 3.10.4 e 3.10.5);</strong><br />
      • <strong>Você declara estar ciente de que a prática de fraude —
      enviar imagem não autêntica (inclusive criada ou alterada por
      inteligência artificial, foto antiga apresentada como atual, imagem
      da internet ou de terceiro, ou foto de medicamento ou receita de
      terceiro), apresentar documento médico falso, adulterado ou que
      imite documento da Plataforma, alterar documento emitido pela
      Plataforma, utilizar documento invalidado ou utilizar identidade de
      terceiro ou documento de identificação falso — sujeita você a MULTA
      DE 7 (SETE) VEZES O VALOR PAGO PELA CONSULTA, comunicada por
      notificação extrajudicial com o meio de pagamento, exigível depois
      do prazo de esclarecimentos, cancelada com restituição integral se
      os esclarecimentos forem acolhidos, e independente das providências
      criminais, que o pagamento não afasta (cláusula 3.10.1);</strong><br />
      • <strong>Você se compromete a que toda foto de caixa, cartela,
      frasco ou embalagem de medicamento, bem como de receita ou
      prescrição, enviada na triagem ou na consulta seja foto pessoal e
      represente fiel e verdadeiramente o uso atual e pessoal do
      medicamento, abstendo-se de induzir o médico a erro (cláusula
      3.10.3); e assume responsabilidade integral e exclusiva pelas
      consequências médicas, à sua própria saúde e jurídicas de induzir
      o médico a erro ou de fornecer informação imprecisa, enganosa ou
      falsa (cláusula 12.4);</strong><br />
      • <strong>Você declara estar ciente de que a data das consultas e dos
      documentos médicos segue o horário oficial de Brasília e o horário de
      início do chat; de que o ajuste da data inicial de atestado por fuso
      horário legal distinto depende de comprovação razoável da localidade
      (ex.: comprovante de residência ou faturas) e é ato médico
      discricionário; de que, em horários limítrofes da virada do dia, a
      verificação técnica do início do cadastro ou da triagem é faculdade do
      médico, não obrigação; e de que a recusa fundamentada do ajuste não dá
      direito a reembolso (cláusulas 3.11 e 3.11.1);</strong><br />
      • <strong>Você declara estar ciente de que, quando a receita for
      renovação de tratamento iniciado por outro médico (médico da
      Plataforma que não seja o prescritor original), a renovação é feita,
      por padrão, para o equivalente a 1 (um) mês de uso do medicamento, e
      de que a aplicação desse padrão não configura falha do serviço nem
      dá direito a reembolso (cláusula 3.12);</strong><br />
      • <strong>Você declara estar ciente de que a Plataforma pode
      invalidar formalmente documentos médicos emitidos (atestados,
      receitas, encaminhamentos, exames e relatórios) em caso de
      violação destes Termos, fraude, lastro em informação ou imagem não
      autêntica ou decisão fundamentada do emissor; compromete-se a não
      apresentar nem utilizar documento invalidado perante empregador,
      escola, autoridade ou qualquer terceiro (inclusive cópia já
      baixada ou impressa); e está ciente de que o descumprimento pode
      ensejar comunicação às autoridades e responsabilização civil
      (cláusulas 3.13, 3.13.1 e 3.13.2);</strong><br />
      • <strong>Você declara que é seu dever comprovar a própria
      identidade mediante o envio de documento oficial quando pedido,
      obriga-se a enviar qualquer documento solicitado pela Plataforma,
      pelo médico responsável ou pelo canal oficial de atendimento, e
      está ciente de que o não envio poderá ensejar, nesses casos, o
      cancelamento dos documentos médicos já emitidos (cláusulas 2.5,
      2.6 e 3.13);</strong><br />
      • <strong>Você declara estar ciente de que, ao usufruir de desconto
      ou benefício promocional vinculado a avaliação em plataforma
      pública de reputação (ex.: Reclame Aqui), arquivar, excluir ou
      ocultar a avaliação, alterar ou rebaixar a nota ou o status de
      resolução, ou inserir nova avaliação sobre o mesmo atendimento,
      sem fundamento objetivo, após o gozo do benefício, constitui
      atitude de má-fé sujeita à cobrança do valor do desconto
      usufruído (inclusive somado à próxima consulta ou por link
      avulso), a multa compensatória de 1 (uma) vez o valor do
      desconto usufruído e à suspensão do uso da Plataforma enquanto
      durar a investigação de conduta indevida (cláusulas 5.6, 5.6.1
      e 5.6.2);</strong>
      <br />
      <br />
      Ao clicar em “Aceitar Termos”, você manifesta seu consentimento
      livre, informado e inequívoco.
    </p>
    {/* TERMS_CONTENT_END */}
  </>
);

export default TermsOfUseContent;
